import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import gsap from 'gsap';
import DJPalProgressBar from './DJPalProgressBar';
import DJPalDateRangeModal from './DJPalDateRangeModal';
import DateRangeSheet from './DateRangeSheet';
import DJPalScrollableList from './DJPalScrollableList';
import DJPalAudioPreview from './DJPalAudioPreview';
import { canPlayMix, useLazyMixResolveMode, useMixPlayback, delayedClick } from './MixPlayer';
import { setArtistQuery, setArtistResults, setDateRange, setActiveSearch, setTrackQuery, setMixQuery, setMixResults, toggleExpandedMix, setMixFilterContext, setTrackListQuery } from '../../redux/slices/djpalSlice';
import { searchArtist, searchTrack, searchMixes, searchTracksList, searchTracksPlayedByArtist, loadMoreArtistMixes, fetchFilteredArtistMixes, fetchFilteredArtistTracks } from '../../redux/thunk/djpalThunk';
import ErrorBanner from '../ErrorBanner';
import CardSearch from './CardSearch';
import ArtistsListModal from './ArtistsListModal';
import CoPlayedTracks from './CoPlayedTracks';
import TracksListModal from './TracksListModal';
import MixesListModal from './MixesListModal';
import LikeButton from '../LikeButton';
import TrackContextMenu from '../TrackContextMenu';
import MixLikeButton from '../MixLikeButton';
import MixContextMenu from '../MixContextMenu';
import useRowContextMenu from '../../hooks/useRowContextMenu';
import SupportedCheck from '../common/SupportedCheck';
import { generateShareImage, shareImage } from '../../utils/generateShareImage';
import { buildArtistShareUrl, copyLink, shareLink } from '../../utils/shareLinks';
import ShareMenu from './ShareMenu';
import { useToast } from '../../contexts/ToastContext';
import { useTierGate } from '../../hooks/useTierGate';
import { showUpgradeModal } from '../../redux/slices/tierSlice';
import { GoatBadge } from '../Tier';
import { isWeb as _isWeb, notifyWebOnly } from '../../utils/webOnly';
import { useIsNarrow } from '../../hooks/useIsNarrow';
import HighlightText from './HighlightText';
import FollowArtistButton from '../FollowArtistButton';
import NotifyBellButton from '../NotifyBellButton';
import MultiArtistText from '../MultiArtistText';
import EntityHoverCard from '../EntityHoverCard';
import { resolveMixCover, useNativeMixCoverMode } from '../../utils/nativeMixCover';
import { entityId } from '../../utils/entityId';
import { safeArtwork } from '../../utils/no1001';
import { track as trackEvent } from '../../utils/analytics';
import TrackMixAppearancesModal from './TrackMixAppearancesModal';
import { checkFollowedArtists } from '../../redux/thunk/followedArtistsThunk';
import { openMixpediaArtistsGrid } from '../../redux/thunk/appNavThunk';
import MixpediaHint from './MixpediaHint';
import ArtistLinksRow, { orderedLinks } from './ArtistLinksRow';
import { ARTIST_HINTS, HINT_KEY, getSeenHints, markHintSeen, getShownHints, markHintShown } from '../../utils/mixpediaHints';
import CoverArt from '../CoverArt';
import { warmImages } from '../../utils/imageWarm';
import { coverUrl } from '../../utils/coverUrl';
import { coverAt } from '../../utils/cover';
import { ReactComponent as PlayIcon } from '../../assets/icons/play.svg';
import { ReactComponent as PauseIcon } from '../../assets/icons/pause.svg';
import { useDesignV2 } from '../../utils/designV2';
import HeroHeader from '../v2/HeroHeader';
import EntityPageSkeleton, { SkelRows } from '../v2/Skeleton';
import AutoLoad, { SortChips } from '../v2/AutoLoad';
import HeroPlayButton from '../v2/HeroPlayButton';
import PageTabs, { SectionHeader } from '../v2/PageTabs';
import { fmtNum, rowCount, relativeDate } from '../v2/format';
import ListToolbar from '../v2/ListToolbar';
import PlayAllButton from '../common/PlayAllButton';

// DESIGN V2 — rows shown per panel on the Overview tab (the page scrolls,
// panels size to content; the full list lives on its own tab / modal).
const V2_OVERVIEW_ROWS = 6;
const V2_TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'tracks', label: 'Tracks' },
    { key: 'mixes', label: 'Mixes' },
    { key: 'network', label: 'Network' },
];

const getUniqueTracks = (mixes) => {
    if (!mixes?.length) return [];
    const seen = new Map();
    const tracks = [];
    for (const mix of mixes) {
        const list = mix.tracklist || mix.tracksIncluded;
        if (!list) continue;
        for (const entry of list) {
            const t = entry.track || entry;
            const key = `${t.title}::${t.artist}`.toLowerCase();
            const prev = seen.get(key);
            if (prev) { prev.mixCount += 1; continue; }
            // DESIGN V2 row context: mixes arrive most-recent-first, so the
            // first mix a track is seen in is its latest play. Shallow copy —
            // the server objects stay untouched.
            const row = { ...t, mixCount: 1, lastMix: mix };
            seen.set(key, row);
            tracks.push(row);
        }
    }
    return tracks;
};

const FREE_RESULTS_LIMIT = 5;

// Minimal artist shape for the full-page artists grid (openMixpediaArtistsGrid)
// — the context rides in nav-history snapshots, so keep it lean.
const toGridArtists = (list) => list.map(a => ({ name: a.name, id: entityId(a) || null, artworkUrl: a.artworkUrl || null }));

const ArtistSearch = () => {
    const dispatch = useDispatch();
    const toast = useToast();
    useNativeMixCoverMode(); // re-render when the native cover flag flips
    const { canAccess } = useTierGate('full-mixpedia');
    // Re-render when the server-sourced lazy-mix mode flips so the
    // `canPlayMix(mix)` calls below pick up the new value.
    useLazyMixResolveMode();
    const { artistResults, isSearching, searchError, dateRange, pendingArtist, artistQuery } = useSelector(state => state.djpal);
    const searchTerm = useSelector(state => state.app.searchTerm);
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
    const firstMatchRef = useRef(null);
    const matchFoundRef = useRef(false);
    const allMatchRefs = useRef([]);
    const matchIndexRef = useRef(0);
    const isWeb = _isWeb;
    const { playMix, queueMix, loadingMixId, playingMixId } = useMixPlayback();

    const handleMixPlay = useCallback((mix, playContext = null) => {
        if (isWeb) {
            notifyWebOnly('mix');
            return;
        }
        playMix(mix, playContext);
    }, [isWeb, playMix]);
    const [showDateModal, setShowDateModal] = useState(false);
    // DESIGN V2 — hero + tab strip (Overview / Tracks / Mixes / Network).
    const v2 = useDesignV2();
    const [v2Tab, setV2TabState] = useState('overview');
    // The progress bar is for THIS page's refinements (date-range re-query over
    // the old results). `isSearching` is global: pressing Back while a track
    // page was still fetching landed here with the flag up and the bar on
    // (Erez 10-29), so the bar is gated on a refinement this page started.
    const [refining, setRefining] = useState(false);
    useEffect(() => { if (!isSearching) setRefining(false); }, [isSearching]);
    const setV2Tab = useCallback((tab, via = 'tab') => {
        setV2TabState((prev) => { if (prev !== tab) trackEvent('artist_page_tab_changed', { tab, via }); return tab; });
    }, []);
    // DESIGN V2 — the Tracks and Mixes tabs are ONE full-width list each with
    // a segment switch (they played / their tracks played by others; by them /
    // featuring them). Overview "See all" lands on the tab with that segment.
    const [v2TracksSeg, setV2TracksSegState] = useState('played');
    const [v2MixesSeg, setV2MixesSegState] = useState('by');
    const setV2TracksSeg = useCallback((seg) => { setV2TracksSegState((prev) => { if (prev !== seg) trackEvent('entity_list_segment_changed', { page: 'artist', list: 'tracks', segment: seg }); return seg; }); }, []);
    // Sort for the tab lists. Tracks: recent (the server's most-recent-mix
    // order) / most played / A–Z. Mixes: recent / A–Z.
    const [v2TracksSort, setV2TracksSortState] = useState('recent');
    const [v2MixesSort, setV2MixesSortState] = useState('recent');
    const setV2TracksSort = useCallback((sort) => { setV2TracksSortState((prev) => { if (prev !== sort) trackEvent('entity_list_sort_changed', { page: 'artist', list: 'tracks', sort }); return sort; }); }, []);
    const setV2MixesSort = useCallback((sort) => { setV2MixesSortState((prev) => { if (prev !== sort) trackEvent('entity_list_sort_changed', { page: 'artist', list: 'mixes', sort }); return sort; }); }, []);
    // Network tab rows expand inline to the co-played tracks (the list modal's
    // expansion, which v2's See all → tab had dropped — Erez 10-29). One open
    // row per list; `fetchCoPlayedTracksFor` is shared with the classic modal.
    const [networkOpenSupport, setNetworkOpenSupport] = useState(null);
    const [networkOpenSupporting, setNetworkOpenSupporting] = useState(null);
    const setV2MixesSeg = useCallback((seg) => { setV2MixesSegState((prev) => { if (prev !== seg) trackEvent('entity_list_segment_changed', { page: 'artist', list: 'mixes', segment: seg }); return seg; }); }, []);
    const [exporting, setExporting] = useState(false);
    const [copiedTrackId, setCopiedTrackId] = useState(null);
    // Which artist-list modal is open (null when closed). Drives both the
    // "Artists they support" and "Artists supporting them" overlays.
    const [artistsModalKind, setArtistsModalKind] = useState(null);
    // Which tracks-list / mixes-list modal is open. One of:
    //   tracks: 'played' | 'by' (subject artist as DJ vs. as track-artist)
    //   mixes:  'byArtist' | 'including'
    const [tracksModalKind, setTracksModalKind] = useState(null);
    const [mixesModalKind, setMixesModalKind] = useState(null);
    // Right-click on a track/mix row opens the same menu as its ⋯ button.
    const { openRowCtx, rowCtxFor } = useRowContextMenu();
    const resultsRef = useRef(null);
    const supportingCardRef = useRef(null);
    const featuringCardRef = useRef(null);
    const mixesByCardRef = useRef(null);
    const followBtnRef = useRef(null);
    const bellBtnRef = useRef(null);
    const shareBtnRef = useRef(null);
    const firstTrackBtnRef = useRef(null);
    const dateRangeBtnRef = useRef(null);
    const datePopoverRef = useRef(null);
    const isNarrow = useIsNarrow();
    const [activeHint, setActiveHint] = useState(null);
    const [hintAutoShow, setHintAutoShow] = useState(false);
    const hintCheckedRef = useRef(false);

    const handleDateApply = useCallback((range) => {
        dispatch(setDateRange(range));
        setShowDateModal(false);
        if (range) {
            trackEvent('mixpedia_date_range_applied', {
                from: 'artist_page',
                has_from: !!range.from,
                has_to: !!range.to,
            });
        }
        if (artistResults?.artistName) {
            // Fresh-load semantics: applying a filter shows the skeleton page,
            // not stale results under a progress bar. Keep the artist id — a
            // name-only re-search is both slower and ambiguous for dupes.
            setRefining(true);
            dispatch(searchArtist({
                artistName: artistResults.artistName,
                artistId: entityId(artistResults?.artist) || null,
                dateRange: range,
            }));
        }
    }, [dispatch, artistResults]);

    const handleArtistClick = useCallback((name, artistId = null, artworkUrl = null) => {
        if (!name) return;
        dispatch(setArtistQuery(name));
        dispatch(searchArtist({ artistName: name, artistId: artistId || null, artworkUrl: artworkUrl || null }));
    }, [dispatch, dateRange]);

    // Group ↔ member links from the server (`members` / `memberOf`, public
    // ids). Clicking a name opens that artist page through the same path as
    // a supporting-artist click. Hidden when the list is empty.
    // A row's "N plays" opens the mixes that played it (Erez 10-30: it used to fall through to the row
    // and open the track). They played = this artist's own mixes; played by others = every play.
    const [playsFor, setPlaysFor] = useState(null);
    const [tp, setTp] = useState({ key: null });
    const [sp, setSp] = useState({ key: null });
    const openPlays = (e, t, list) => {
        e.stopPropagation();
        trackEvent('track_plays_opened', { list, from: 'artist_tracks' });
        const me = artist ? { name: artist.name, id: entityId(artist) } : null;
        setPlaysFor({ track: t, subjectArtist: list === 'played' ? me : null, excludeArtist: list === 'others' ? me : null });
    };
    // Hover preview (photo + genre + Follow) on artist credit links — same
    // card as the For You feed / mix page. Touch renders `node` untouched.
    const artistHoverWrap = useCallback((a, node) => (
        <EntityHoverCard
            type="artist"
            entity={a}
            onNavigate={() => handleArtistClick(a.name, entityId(a), a.artworkUrl)}
        >
            {node}
        </EntityHoverCard>
    ), [handleArtistClick]);

    // Like "Supported by": the first two names (hover card + click through), then
    // "and N more", which opens the full list in place; long names clip with an ellipsis.
    const [membersOpen, setMembersOpen] = useState({});
    const renderMemberLine = (label, list) => {
        const names = Array.isArray(list) ? list.filter((m) => m && m.name) : [];
        if (names.length === 0) return null;
        const openKey = `${label}:${entityId(artist) || artist?.name || ''}`;
        const open = !!membersOpen[openKey];
        const shown = open ? names : names.slice(0, 2);
        return (
            <div className="artist-genre artist-members-line">
                <span className="supported-grey">{label}: </span>
                {shown.map((m, i) => (
                    <React.Fragment key={entityId(m) || m.name}>
                        {i > 0 && <span className="supported-grey">, </span>}
                        {artistHoverWrap(m, (
                            <span className="supported-name artist-member-name" title={m.name} onClick={() => handleArtistClick(m.name, entityId(m))}>{m.name}</span>
                        ))}
                    </React.Fragment>
                ))}
                {!open && names.length > 2 && (
                    <>
                        <span className="supported-grey"> and </span>
                        <span className="supported-others" onClick={() => setMembersOpen((o) => ({ ...o, [openKey]: true }))}>{fmtNum(names.length - 2)} more</span>
                    </>
                )}
            </div>
        );
    };


    const handleMixClick = useCallback((mix) => {
        const djLabel = mix.djName || mix.artist || '';
        const title = mix.title || '';
        const displayQuery = djLabel && title ? `${djLabel} - ${title}` : title;
        // Id-only navigation (link = exact-key fallback); clicks never
        // text-search.
        const filter = mix.id ? { id: mix.id }
            : mix.publicId ? { public_id: mix.publicId }
            : mix.link ? { link: mix.link }
            : null;
        if (!filter) return;
        dispatch(setMixQuery(displayQuery));
        dispatch(setActiveSearch('mix'));
        dispatch(searchMixes({ filters: filter }));
    }, [dispatch]);

    const handleSearchTrack = useCallback((e, track) => {
        e.stopPropagation();
        const query = track.title && track.artist ? `${track.artist} - ${track.title}` : (track.title || '');
        dispatch(setTrackQuery(query));
        dispatch(setActiveSearch('track'));
        dispatch(searchTrack({ trackName: query, trackId: entityId(track), variant: 'A', artworkUrl: track.artwork || track.artworkUrl || track.image || null }));
    }, [dispatch, dateRange]);

    const handleCopyTrack = useCallback((e, track, trackId) => {
        e.stopPropagation();
        const text = track.artist ? `${track.artist} - ${track.title}` : (track.title || '');
        navigator.clipboard.writeText(text);
        setCopiedTrackId(trackId);
        setTimeout(() => setCopiedTrackId(null), 1500);
    }, []);

    const paginationByArtist = artistResults?.paginationByArtist;
    const paginationIncluding = artistResults?.paginationIncluding;
    const [loadingMoreBy, setLoadingMoreBy] = useState(false);
    const [loadingMoreIncl, setLoadingMoreIncl] = useState(false);
    const TRACKS_PAGE_SIZE = 50;
    const [tracksPlayedLimit, setTracksPlayedLimit] = useState(TRACKS_PAGE_SIZE);
    const [tracksOthersLimit, setTracksOthersLimit] = useState(TRACKS_PAGE_SIZE);
    // Network tab: the two artist lists are pages of 50 with "Load more" (the
    // filter searches the whole list). Rendering all 2,000+ bubbles at once
    // froze the page for a second and made it 100k px tall — Erez 10-28.
    const NETWORK_PAGE_SIZE = 50;
    const [networkSupportLimit, setNetworkSupportLimit] = useState(NETWORK_PAGE_SIZE);
    const [networkSupportersLimit, setNetworkSupportersLimit] = useState(NETWORK_PAGE_SIZE);
    // Network tab sort (what the old "See all" modal offered): the server's
    // popularity order, last played first, or A–Z.
    const [networkSort, setNetworkSort] = useState('popularity');
    const NETWORK_SORTS = ['popularity', 'recent', 'alphabetical'];
    const NETWORK_SORT_LABELS = { popularity: 'Popularity', recent: 'Recent activity', alphabetical: 'A–Z' };
    const sortArtists = (list) => networkSort === 'recent'
        ? [...list].sort((a, b) => { const ad = a.lastPlayedDate || '', bd = b.lastPlayedDate || ''; if (ad === bd) return 0; if (!ad) return 1; if (!bd) return -1; return bd.localeCompare(ad); })
        : networkSort === 'alphabetical' ? [...list].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })) : list;
    const [loadingMoreTracksBy, setLoadingMoreTracksBy] = useState(false);
    const [loadingMoreTracksIncl, setLoadingMoreTracksIncl] = useState(false);

    // In-card filter inputs. Mix-card filters fetch from the server (title
    // secondary filter); track-card filters use a server endpoint scoped to
    // the artist; artist-bubble filters run client-side over fully-loaded
    // arrays.
    const [mixesByQuery, setMixesByQuery] = useState('');
    const [mixesInclQuery, setMixesInclQuery] = useState('');
    const [tracksByQuery, setTracksByQuery] = useState('');
    const [tracksOthersQuery, setTracksOthersQuery] = useState('');
    const [artistsTheySupportQuery, setArtistsTheySupportQuery] = useState('');
    const [artistsSupportingThemQuery, setArtistsSupportingThemQuery] = useState('');
    // Filtered mix results: { mixes, pagination, loading } | null
    const [mixesByFiltered, setMixesByFiltered] = useState(null);
    const [mixesInclFiltered, setMixesInclFiltered] = useState(null);
    const [loadingMoreByFiltered, setLoadingMoreByFiltered] = useState(false);
    const [loadingMoreInclFiltered, setLoadingMoreInclFiltered] = useState(false);
    // Filtered track results from server: { tracks, pagination, loading } | null
    const [tracksByResults, setTracksByResults] = useState(null);
    const [tracksOthersResults, setTracksOthersResults] = useState(null);
    const [loadingMoreTracksByFiltered, setLoadingMoreTracksByFiltered] = useState(false);
    const [loadingMoreTracksOthersFiltered, setLoadingMoreTracksOthersFiltered] = useState(false);

    const scrollToCard = useCallback((ref) => {
        const el = ref?.current;
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('highlight-spin');
        setTimeout(() => el.classList.remove('highlight-spin'), 2500);
    }, []);
    const handleOthersClick = useCallback(() => scrollToCard(supportingCardRef), [scrollToCard]);
    const handleFeaturingClick = useCallback(() => scrollToCard(featuringCardRef), [scrollToCard]);
    const handleMixesByClick = useCallback(() => scrollToCard(mixesByCardRef), [scrollToCard]);

    // The page's artist public id — every mixes request this page makes
    // (load more, in-card filter, Show all, retry) carries it, so page 2 /
    // a title filter can never resolve the NAME to a namesake (Karotte →
    // Kaiserdisco, audit 2026-10-20). Mixpedia links entities by id.
    const artistPublicId = useMemo(() => entityId(artistResults?.artist), [artistResults?.artist]);

    const handleLoadMoreByArtist = useCallback(async () => {
        if (loadingMoreBy || !paginationByArtist?.hasNext) return;
        setLoadingMoreBy(true);
        const artistName = artistResults?.artistName;
        await dispatch(loadMoreArtistMixes({
            section: 'byArtist',
            filters: { artist: artistName, ...(artistPublicId ? { artist_id: artistPublicId } : {}) },
            dateRange,
            page: (paginationByArtist?.page || 1) + 1,
        }));
        setLoadingMoreBy(false);
    }, [dispatch, loadingMoreBy, paginationByArtist, artistResults, artistPublicId, dateRange]);

    const handleLoadMoreIncluding = useCallback(async () => {
        if (loadingMoreIncl || !paginationIncluding?.hasNext) return;
        setLoadingMoreIncl(true);
        const artistName = artistResults?.artistName;
        await dispatch(loadMoreArtistMixes({
            section: 'including',
            filters: { artistIncluded: artistName, ...(artistPublicId ? { artist_id: artistPublicId } : {}) },
            dateRange,
            page: (paginationIncluding?.page || 1) + 1,
        }));
        setLoadingMoreIncl(false);
    }, [dispatch, loadingMoreIncl, paginationIncluding, artistResults, artistPublicId, dateRange]);

    // Close date popover on outside click
    useEffect(() => {
        if (!showDateModal) return undefined;
        const onDown = (e) => {
            if (datePopoverRef.current && !datePopoverRef.current.contains(e.target)) {
                setShowDateModal(false);
            }
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [showDateModal]);

    // Reset track pagination on new artist
    const prevArtistRef = useRef(null);
    useEffect(() => {
        const name = artistResults?.artistName;
        if (name && name !== prevArtistRef.current) {
            prevArtistRef.current = name;
            setTracksPlayedLimit(TRACKS_PAGE_SIZE);
            setTracksOthersLimit(TRACKS_PAGE_SIZE);
            setNetworkSupportLimit(NETWORK_PAGE_SIZE);
            setNetworkSupportersLimit(NETWORK_PAGE_SIZE);
            setV2TabState('overview');
            setV2TracksSegState('played');
            setV2MixesSegState('by');
            setV2TracksSortState('recent');
            setV2MixesSortState('recent');
            setNetworkOpenSupport(null);
            setNetworkOpenSupporting(null);
        }
    }, [artistResults?.artistName]);

    const animatedArtistRef = useRef(null);
    useEffect(() => {
        const currentArtist = artistResults?.artistName;
        if (artistResults && resultsRef.current && currentArtist !== animatedArtistRef.current) {
            animatedArtistRef.current = currentArtist;
            // Skip the header (first child): the pending-search state already
            // painted it. Quick dissolve only — the skeleton already drew the
            // page structure, so a staggered slide-in reads as a full reload.
            const items = Array.prototype.slice.call(resultsRef.current.children, 1);
            if (items?.length) {
                gsap.fromTo(items,
                    { opacity: 0 },
                    { opacity: 1, duration: 0.25, ease: 'power1.out' }
                );
            }
        }
    }, [artistResults]);

    // Check if the displayed artist is followed
    useEffect(() => {
        if (artistResults && isAuthenticated) {
            const artistName = artistResults?.artist?.name || artistResults?.artistName;
            if (artistName) {
                dispatch(checkFollowedArtists([artistName]));
            }
        }
    }, [artistResults, isAuthenticated, dispatch]);

    const mixesByArtist = artistResults?.mixesByArtist || [];
    const mixesIncludingArtist = artistResults?.mixesIncludingArtist || [];
    const artist = artistResults?.artist;

    // Which of the subject's tracks a DJ played (isSupporting) or which of an
    // artist's tracks the subject played. The two modes swap DJ vs track-artist.
    const fetchCoPlayedTracksFor = useCallback(async (a, isSupporting) => {
        const token = localStorage.getItem('djpal:token');
        if (!token) return { tracks: [], total: 0 };
        const subjectPublicId = entityId(artist) || null;
        const subjectName = artist?.name || artistResults?.artistName || '';
        const aPublicId = entityId(a);
        const opts = isSupporting
            ? { djArtistPublicId: aPublicId, djArtistName: a.name, trackArtistPublicId: subjectPublicId, trackArtistName: subjectName }
            : { djArtistPublicId: subjectPublicId, djArtistName: subjectName, trackArtistPublicId: aPublicId, trackArtistName: a.name };
        const result = await window.electron.djpal.searchCoPlayedTracks(token, { ...opts, page: 1, limit: 50, dateRange });
        if (result?.error) throw new Error(result.error);
        const data = result?.data || result;
        return { tracks: data?.results || [], total: data?.pagination?.totalCount ?? null };
    }, [artist, artistResults?.artistName, dateRange]);
    const statsByArtist = artistResults?.statsByArtist || {};
    const statsIncluded = artistResults?.statsIncluded || {};
    const supportingArtists = artistResults?.supportingArtists || [];
    const hasExternalLinks = orderedLinks(artist?.externalIds).length > 0;
    const artistsSupporting = artistResults?.artistsSupporting || [];
    // Under design v2 the two arrays are only the top 50 (supportLimit); these are the full sizes.
    const supportingArtistsTotal = artistResults?.supportingArtistsTotal ?? supportingArtists.length;
    const artistsSupportingTotal = artistResults?.artistsSupportingTotal ?? artistsSupporting.length;

    const tracksPlayedAll = getUniqueTracks(mixesByArtist);
    const tracksPlayedByOthersAll = getUniqueTracks(mixesIncludingArtist);

    // The two in-card track lists page CLIENT-SIDE (scroll-bottom bumps a
    // slice limit over data already in memory), so their covers were never
    // requested until each row scrolled into the 300px card — the "covers
    // trail the text" effect. Keep two pages of covers warmed AHEAD of the
    // current limit; warmImages dedupes, so each bump only fetches the new
    // slice. Also covers the mixes cards' initial page via the same arrays.
    // Modest lookahead only — a big warm burst on page open competes with the
    // covers actually on screen (measured: 239 queued requests, 1s+ even on
    // cache hits). Warms are fetchPriority:'low' (imageWarm.js) and one page
    // deep; each limit bump warms just the next slice (dedupe skips the rest).
    useEffect(() => {
        const ahead = (arr, limit) => arr.slice(0, limit + TRACKS_PAGE_SIZE)
            .map((t) => coverUrl(t.artwork || t.artworkUrl || t.image || null, 48));
        warmImages(ahead(tracksPlayedAll, tracksPlayedLimit), 80);
        warmImages(ahead(tracksPlayedByOthersAll, tracksOthersLimit), 80);
        warmImages((mixesByArtist || []).slice(0, 15).map((m) => coverUrl(resolveMixCover(m), 48)), 30);
        warmImages((mixesIncludingArtist || []).slice(0, 15).map((m) => coverUrl(resolveMixCover(m), 48)), 30);
        // The supporting-artist bubbles (24px avatars in the two 250px
        // cards) — without a warm they cold-fetch one by one at render.
        warmImages(supportingArtists.slice(0, 24).map((a) => coverUrl(a.artworkUrl, 24)), 24);
        warmImages(artistsSupporting.slice(0, 24).map((a) => coverUrl(a.artworkUrl, 24)), 24);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tracksPlayedAll.length, tracksPlayedByOthersAll.length, tracksPlayedLimit, tracksOthersLimit, mixesByArtist, mixesIncludingArtist, supportingArtists.length, artistsSupporting.length]);

    // "Show all" handlers — open dedicated views for the per-section data.
    // Mirrors the goArtistMixesBy / goArtistFeaturing / goTrackSearch pattern
    // used by SearchResultsPage so the Mixes / Tracks tabs render the same
    // header and pagination affordances users already know.
    const artistName = artist?.name || artistResults?.artistName;

    const handleShowAllMixesBy = useCallback(() => {
        if (!artistName) return;
        const filters = artistPublicId ? { artist: artistName, artist_id: artistPublicId } : { artist: artistName };
        dispatch(setActiveSearch('mix'));
        dispatch(setMixQuery(artistName));
        dispatch(setMixFilterContext({ kind: 'by', artistName, artistPublicId: artistPublicId || null }));
        dispatch(searchMixes({ filters }));
    }, [dispatch, artistName, artistPublicId]);

    const handleShowAllMixesFeaturing = useCallback(() => {
        if (!artistName) return;
        const filters = artistPublicId ? { artistIncluded: artistName, artist_id: artistPublicId } : { artistIncluded: artistName };
        dispatch(setActiveSearch('mix'));
        dispatch(setMixQuery(artistName));
        dispatch(setMixFilterContext({ kind: 'featuring', artistName, artistPublicId: artistPublicId || null }));
        dispatch(searchMixes({ filters }));
    }, [dispatch, artistName, artistPublicId]);

    // "Their tracks played by others" — full list of the artist's OWN tracks
    // that appeared in mixes (relation 'by'), ordered by most-recent-mix DESC
    // to match the on-page preview. (Was a generic track-name FTS search, which
    // returned a different dataset in a different order.)
    const handleShowAllTracksByOthers = useCallback(() => {
        if (!artistName) return;
        dispatch(setActiveSearch('trackList'));
        dispatch(setTrackListQuery(''));
        dispatch(searchTracksPlayedByArtist({ relation: 'by', artistName, artistPublicId, dateRange }));
    }, [dispatch, artistName, artistPublicId, dateRange]);

    // "Tracks they played" — full list of tracks the artist played as a DJ
    // (relation 'played'), same most-recent-mix-first order as the preview.
    const handleShowAllTracksPlayed = useCallback(() => {
        if (!artistName) return;
        dispatch(setActiveSearch('trackList'));
        dispatch(setTrackListQuery(''));
        dispatch(searchTracksPlayedByArtist({ relation: 'played', artistName, artistPublicId, dateRange }));
    }, [dispatch, artistName, artistPublicId, dateRange]);

    // In-page modal openers (sibling to the "Show all" buttons above). These
    // pop a single-column list with expandable rows in the same style as the
    // artists modal, without leaving the artist page.
    const handleOpenMixesByModal = useCallback(() => {
        if (!artistName) return;
        setMixesModalKind('byArtist');
    }, [artistName]);

    const handleOpenMixesFeaturingModal = useCallback(() => {
        if (!artistName) return;
        setMixesModalKind('including');
    }, [artistName]);

    const handleOpenTracksByOthersModal = useCallback(() => {
        if (!artistName) return;
        setTracksModalKind('by');
    }, [artistName]);

    const handleOpenTracksPlayedModal = useCallback(() => {
        if (!artistName) return;
        setTracksModalKind('played');
    }, [artistName]);

    // DESIGN V2 "See all": the full list is the tab, not an overlay.
    const v2OpenTracks = useCallback((seg) => { setV2TracksSegState(seg); setV2Tab('tracks', 'see_all'); }, [setV2Tab]);
    const v2OpenMixes = useCallback((seg) => { setV2MixesSegState(seg); setV2Tab('mixes', 'see_all'); }, [setV2Tab]);

    // Per-artist track counts come from the server now (`tracksCount` on each
    // supporting / supported artist) — accurate across all mixes, not just
    // the loaded page. Public-API sanitizer strips internal IDs and
    // artistIds arrays, so a client-side aggregate would be either wrong or
    // a lower bound; rely on the server-computed value instead.

    const tracksPlayed = canAccess ? tracksPlayedAll.slice(0, tracksPlayedLimit) : tracksPlayedAll.slice(0, FREE_RESULTS_LIMIT);
    const tracksPlayedByOthers = canAccess ? tracksPlayedByOthersAll.slice(0, tracksOthersLimit) : tracksPlayedByOthersAll.slice(0, FREE_RESULTS_LIMIT);
    const displayedMixesByArtist = canAccess ? mixesByArtist : mixesByArtist.slice(0, FREE_RESULTS_LIMIT);
    const displayedMixesIncludingArtist = canAccess ? mixesIncludingArtist : mixesIncludingArtist.slice(0, FREE_RESULTS_LIMIT);
    // Artist-bubble client-side filter (lists are fully loaded — no
    // pagination — so filtering is a substring match on the name).
    const filterByName = (list, q) => q.trim()
        ? list.filter(a => (a.name || '').toLowerCase().includes(q.trim().toLowerCase()))
        : list;
    const artistsSupportingFiltered = filterByName(artistsSupporting, artistsTheySupportQuery);
    const supportingArtistsFiltered = filterByName(supportingArtists, artistsSupportingThemQuery);
    const displayedArtistsSupporting = canAccess ? artistsSupportingFiltered : artistsSupportingFiltered.slice(0, FREE_RESULTS_LIMIT);
    const displayedSupportingArtists = canAccess ? supportingArtistsFiltered : supportingArtistsFiltered.slice(0, FREE_RESULTS_LIMIT);

    const handleLoadMoreTracksPlayed = useCallback(async () => {
        if (loadingMoreTracksBy) return;
        if (tracksPlayedLimit < tracksPlayedAll.length) {
            setTracksPlayedLimit(prev => prev + TRACKS_PAGE_SIZE);
            return;
        }
        if (paginationByArtist?.hasNext) {
            setLoadingMoreTracksBy(true);
            await handleLoadMoreByArtist();
            setTracksPlayedLimit(prev => prev + TRACKS_PAGE_SIZE);
            setLoadingMoreTracksBy(false);
        }
    }, [loadingMoreTracksBy, tracksPlayedLimit, tracksPlayedAll.length, paginationByArtist, handleLoadMoreByArtist]);

    const handleLoadMoreTracksOthers = useCallback(async () => {
        if (loadingMoreTracksIncl) return;
        if (tracksOthersLimit < tracksPlayedByOthersAll.length) {
            setTracksOthersLimit(prev => prev + TRACKS_PAGE_SIZE);
            return;
        }
        if (paginationIncluding?.hasNext) {
            setLoadingMoreTracksIncl(true);
            await handleLoadMoreIncluding();
            setTracksOthersLimit(prev => prev + TRACKS_PAGE_SIZE);
            setLoadingMoreTracksIncl(false);
        }
    }, [loadingMoreTracksIncl, tracksOthersLimit, tracksPlayedByOthersAll.length, paginationIncluding, handleLoadMoreIncluding]);

    // Reset all in-card filters when the artist changes
    useEffect(() => {
        setMixesByQuery('');
        setMixesInclQuery('');
        setTracksByQuery('');
        setTracksOthersQuery('');
        setArtistsTheySupportQuery('');
        setArtistsSupportingThemQuery('');
        setMixesByFiltered(null);
        setMixesInclFiltered(null);
        setTracksByResults(null);
        setTracksOthersResults(null);
    }, [artistResults?.artistName]);

    // Debounced server-side filter for "Mixes by them"
    useEffect(() => {
        const q = mixesByQuery.trim();
        const artistName = artistResults?.artistName;
        if (!q || !artistName) {
            setMixesByFiltered(null);
            return undefined;
        }
        let cancelled = false;
        setMixesByFiltered({ mixes: [], pagination: null, loading: true });
        const timer = setTimeout(async () => {
            try {
                const result = await dispatch(fetchFilteredArtistMixes({
                    section: 'byArtist', artistName, artistId: artistPublicId, query: q, dateRange, page: 1,
                })).unwrap();
                if (cancelled) return;
                setMixesByFiltered({ mixes: result.mixes, pagination: result.pagination, loading: false });
            } catch (err) {
                if (cancelled) return;
                console.error('Filter Mixes by them failed:', err);
                setMixesByFiltered({ mixes: [], pagination: null, loading: false });
            }
        }, 250);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [mixesByQuery, artistResults?.artistName, artistPublicId, dateRange, dispatch]);

    // Debounced server-side filter for "Mixes featuring them"
    useEffect(() => {
        const q = mixesInclQuery.trim();
        const artistName = artistResults?.artistName;
        if (!q || !artistName) {
            setMixesInclFiltered(null);
            return undefined;
        }
        let cancelled = false;
        setMixesInclFiltered({ mixes: [], pagination: null, loading: true });
        const timer = setTimeout(async () => {
            try {
                const result = await dispatch(fetchFilteredArtistMixes({
                    section: 'including', artistName, artistId: artistPublicId, query: q, dateRange, page: 1,
                })).unwrap();
                if (cancelled) return;
                setMixesInclFiltered({ mixes: result.mixes, pagination: result.pagination, loading: false });
            } catch (err) {
                if (cancelled) return;
                console.error('Filter Mixes featuring them failed:', err);
                setMixesInclFiltered({ mixes: [], pagination: null, loading: false });
            }
        }, 250);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [mixesInclQuery, artistResults?.artistName, artistPublicId, dateRange, dispatch]);

    const handleLoadMoreByFiltered = useCallback(async () => {
        const f = mixesByFiltered;
        const artistName = artistResults?.artistName;
        if (!f || loadingMoreByFiltered || !f.pagination?.hasNext || !artistName) return;
        setLoadingMoreByFiltered(true);
        try {
            const result = await dispatch(fetchFilteredArtistMixes({
                section: 'byArtist', artistName, artistId: artistPublicId, query: mixesByQuery.trim(), dateRange,
                page: (f.pagination.page || 1) + 1,
            })).unwrap();
            setMixesByFiltered(prev => prev ? {
                mixes: [...prev.mixes, ...result.mixes],
                pagination: result.pagination,
                loading: false,
            } : prev);
        } catch (err) {
            console.error('Load more filtered (by) failed:', err);
        } finally {
            setLoadingMoreByFiltered(false);
        }
    }, [dispatch, mixesByFiltered, loadingMoreByFiltered, artistResults, artistPublicId, mixesByQuery, dateRange]);

    const handleLoadMoreInclFiltered = useCallback(async () => {
        const f = mixesInclFiltered;
        const artistName = artistResults?.artistName;
        if (!f || loadingMoreInclFiltered || !f.pagination?.hasNext || !artistName) return;
        setLoadingMoreInclFiltered(true);
        try {
            const result = await dispatch(fetchFilteredArtistMixes({
                section: 'including', artistName, artistId: artistPublicId, query: mixesInclQuery.trim(), dateRange,
                page: (f.pagination.page || 1) + 1,
            })).unwrap();
            setMixesInclFiltered(prev => prev ? {
                mixes: [...prev.mixes, ...result.mixes],
                pagination: result.pagination,
                loading: false,
            } : prev);
        } catch (err) {
            console.error('Load more filtered (incl) failed:', err);
        } finally {
            setLoadingMoreInclFiltered(false);
        }
    }, [dispatch, mixesInclFiltered, loadingMoreInclFiltered, artistResults, artistPublicId, mixesInclQuery, dateRange]);

    // Debounced server-side filter for "Tracks they played"
    useEffect(() => {
        const q = tracksByQuery.trim();
        const artistName = artistResults?.artistName;
        const artistPublicId = entityId(artistResults?.artist);
        if (!q || !artistName) {
            setTracksByResults(null);
            return undefined;
        }
        let cancelled = false;
        setTracksByResults({ tracks: [], pagination: null, loading: true });
        const timer = setTimeout(async () => {
            try {
                const result = await dispatch(fetchFilteredArtistTracks({
                    relation: 'played', artistName, artistPublicId, query: q, page: 1,
                })).unwrap();
                if (cancelled) return;
                setTracksByResults({ tracks: result.tracks, pagination: result.pagination, loading: false });
            } catch (err) {
                if (cancelled) return;
                console.error('Filter Tracks they played failed:', err);
                setTracksByResults({ tracks: [], pagination: null, loading: false });
            }
        }, 250);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [tracksByQuery, artistResults?.artistName, artistResults?.artist?.publicId, artistResults?.artist?.id, dispatch]);

    // Debounced server-side filter for "Their tracks played by others"
    useEffect(() => {
        const q = tracksOthersQuery.trim();
        const artistName = artistResults?.artistName;
        const artistPublicId = entityId(artistResults?.artist);
        if (!q || !artistName) {
            setTracksOthersResults(null);
            return undefined;
        }
        let cancelled = false;
        setTracksOthersResults({ tracks: [], pagination: null, loading: true });
        const timer = setTimeout(async () => {
            try {
                const result = await dispatch(fetchFilteredArtistTracks({
                    relation: 'by', artistName, artistPublicId, query: q, page: 1,
                })).unwrap();
                if (cancelled) return;
                setTracksOthersResults({ tracks: result.tracks, pagination: result.pagination, loading: false });
            } catch (err) {
                if (cancelled) return;
                console.error('Filter Their tracks played by others failed:', err);
                setTracksOthersResults({ tracks: [], pagination: null, loading: false });
            }
        }, 250);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [tracksOthersQuery, artistResults?.artistName, artistResults?.artist?.publicId, artistResults?.artist?.id, dispatch]);

    const handleLoadMoreTracksByFiltered = useCallback(async () => {
        const f = tracksByResults;
        const artistName = artistResults?.artistName;
        const artistPublicId = entityId(artistResults?.artist);
        if (!f || loadingMoreTracksByFiltered || !f.pagination?.hasNext || !artistName) return;
        setLoadingMoreTracksByFiltered(true);
        try {
            const result = await dispatch(fetchFilteredArtistTracks({
                relation: 'played', artistName, artistPublicId, query: tracksByQuery.trim(),
                page: (f.pagination.page || 1) + 1,
            })).unwrap();
            setTracksByResults(prev => prev ? {
                tracks: [...prev.tracks, ...result.tracks],
                pagination: result.pagination,
                loading: false,
            } : prev);
        } catch (err) {
            console.error('Load more filtered tracks (played) failed:', err);
        } finally {
            setLoadingMoreTracksByFiltered(false);
        }
    }, [dispatch, tracksByResults, loadingMoreTracksByFiltered, artistResults, tracksByQuery]);

    const handleLoadMoreTracksOthersFiltered = useCallback(async () => {
        const f = tracksOthersResults;
        const artistName = artistResults?.artistName;
        const artistPublicId = entityId(artistResults?.artist);
        if (!f || loadingMoreTracksOthersFiltered || !f.pagination?.hasNext || !artistName) return;
        setLoadingMoreTracksOthersFiltered(true);
        try {
            const result = await dispatch(fetchFilteredArtistTracks({
                relation: 'by', artistName, artistPublicId, query: tracksOthersQuery.trim(),
                page: (f.pagination.page || 1) + 1,
            })).unwrap();
            setTracksOthersResults(prev => prev ? {
                tracks: [...prev.tracks, ...result.tracks],
                pagination: result.pagination,
                loading: false,
            } : prev);
        } catch (err) {
            console.error('Load more filtered tracks (by) failed:', err);
        } finally {
            setLoadingMoreTracksOthersFiltered(false);
        }
    }, [dispatch, tracksOthersResults, loadingMoreTracksOthersFiltered, artistResults, tracksOthersQuery]);

    // Track-card filter results are populated by a server fetch (see effects
    // above) when a query is active. When inactive, both stay null and the
    // unfiltered tracks lists are rendered instead.
    const tracksPlayedFiltered = tracksByResults?.tracks ?? null;
    const tracksOthersFiltered = tracksOthersResults?.tracks ?? null;
    const tracksBySearching = !!tracksByQuery.trim() && !!tracksByResults?.loading;
    const tracksOthersSearching = !!tracksOthersQuery.trim() && !!tracksOthersResults?.loading;

    // Highlight helper
    allMatchRefs.current = [];
    matchFoundRef.current = false;
    const hl = (text) => {
        if (!searchTerm || !text) return text || '';
        const lower = (text || '').toLowerCase();
        const idx = lower.indexOf(searchTerm.toLowerCase());
        if (idx === -1) return text;
        const ref = (el) => { if (el) allMatchRefs.current.push(el); };
        const isFirst = !matchFoundRef.current;
        if (isFirst) matchFoundRef.current = true;
        return <HighlightText text={text} search={searchTerm} matchRef={isFirst ? (el) => { firstMatchRef.current = el; ref(el); } : ref} />;
    };

    useEffect(() => {
        if (searchTerm && firstMatchRef.current) {
            matchIndexRef.current = 0;
            firstMatchRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [searchTerm]);

    // Enter key: jump to next/prev match
    useEffect(() => {
        const handleNextMatch = (e) => {
            const matches = allMatchRefs.current;
            if (!matches.length) return;
            matchIndexRef.current = (matchIndexRef.current + e.detail + matches.length) % matches.length;
            matches[matchIndexRef.current]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        };
        window.addEventListener('search-next-match', handleNextMatch);
        return () => window.removeEventListener('search-next-match', handleNextMatch);
    }, []);

    const handleExport = useCallback(async () => {
        if (exporting) return;
        setExporting(true);
        try {
            const name = artist?.name || artistResults?.artistName || 'Artist';
            const artworkUrl = artist?.artworkUrl;
            const allStats = [];
            if (statsIncluded.totalTracksSupported != null) allStats.push({ value: statsIncluded.totalTracksSupported, label: 'Tracks with DJ Support' });
            if (statsIncluded.totalSupporting != null) allStats.push({ value: statsIncluded.totalSupporting, label: 'Supporting DJs' });
            if (statsIncluded.totalAppearances != null) allStats.push({ value: statsIncluded.totalAppearances, label: 'Total DJ Plays' });
            if (statsIncluded.mixesIncluded != null) allStats.push({ value: statsIncluded.mixesIncluded, label: 'Mixes Featuring Them' });
            if (allStats.length < 4 && (statsByArtist.totalAppearances > 0 || mixesByArtist.length > 0)) allStats.push({ value: statsByArtist.totalAppearances ?? paginationByArtist?.totalCount ?? mixesByArtist.length, label: 'Mixes Performed' });
            if (allStats.length < 4 && (statsByArtist.totalTracksPlayed != null || tracksPlayed.length > 0)) allStats.push({ value: statsByArtist.totalTracksPlayed ?? tracksPlayed.length, label: 'Tracks Played' });

            const supSlice = supportingArtists.slice(0, 6);
            const supNames = supSlice.map(a => a.name);
            const supArtworkUrls = supSlice.map(a => a.artworkUrl || '');
            const totalOthers = Math.max(0, supportingArtistsTotal - 6);

            // Mirror the page header: styles as the white byline under the
            // name, "Featured in N mixes" as the grey meta. Zero-stats
            // artists get a follow CTA instead of a row of zeros.
            const nonZeroStats = allStats.filter(s => s.value);
            const featuredCount = statsIncluded?.mixesIncluded ?? 0;
            const blob = await generateShareImage({
                type: 'artist',
                name,
                byline: artist?.styles?.length > 0 ? artist.styles.slice(0, 3).join(', ') : undefined,
                artworkUrl,
                metaLine1: featuredCount > 0 ? `Featured in ${featuredCount.toLocaleString()} mixes` : undefined,
                stats: nonZeroStats.slice(0, 4),
                emptyMessage: nonZeroStats.length === 0
                    ? { headline: 'Still under the radar.', cta: 'Follow them on MixGoat and ring the bell — hear their first drop before anyone else.' }
                    : undefined,
                supportedBy: supNames.length > 0 ? { names: supNames, artworkUrls: supArtworkUrls, totalOthers } : undefined,
            });
            const { method, filePath } = await shareImage(blob, `${name.replace(/[^a-zA-Z0-9]/g, '_')}_impact.png`);
            const savedFolder = filePath ? filePath.split('/').slice(-2)[0] : null;
            if (method === 'clipboard') toast.success(`"${name}" support summary copied to clipboard`);
            else if (method === 'downloaded') toast.success(`"${name}" support summary saved to Downloads`);
            else if (method === 'saved') toast.success(`"${name}" support summary saved${savedFolder ? ` to ${savedFolder}` : ''}`);
            trackEvent('share_image_created', { entity_type: 'artist', method });
        } catch (err) {
            console.error('Export failed:', err);
            toast.error('Could not share image');
        } finally {
            setExporting(false);
        }
    }, [exporting, artist, artistResults, statsIncluded, statsByArtist, mixesByArtist, tracksPlayed, supportingArtists, supportingArtistsTotal, toast]);

    const handleCopyLink = useCallback(async () => {
        const name = artist?.name || artistResults?.artistName;
        const url = buildArtistShareUrl(name);
        if (!url) { toast.error('No shareable link yet'); return 'failed'; }
        const res = await copyLink(url);
        if (res === 'copied') {
            toast.success('Link copied');
            trackEvent('share_link_copied', { entity_type: 'artist', method: 'copy' });
        } else if (res === 'failed') toast.error('Could not copy link');
        return res;
    }, [artist, artistResults, toast]);

    // Mobile "Share page" — open the OS share sheet for the canonical URL.
    const handleSharePage = useCallback(async () => {
        const name = artist?.name || artistResults?.artistName;
        const url = buildArtistShareUrl(name);
        if (!url) { toast.error('No shareable link yet'); return; }
        const res = await shareLink(url, name);
        if (res === 'copied') toast.success('Link copied');
        else if (res === 'failed') { toast.error('Could not share'); return; }
        trackEvent('share_link_copied', { entity_type: 'artist', method: 'native_share' });
    }, [artist, artistResults, toast]);

    const hasResults = artistResults && (mixesByArtist.length > 0 || mixesIncludingArtist.length > 0);
    const noResults = artistResults && mixesByArtist.length === 0 && mixesIncludingArtist.length === 0;
    const noResultsInRange = noResults && !!dateRange && (dateRange.from || dateRange.to);

    useEffect(() => {
        window.__resetMixpediaHints = () => {
            localStorage.removeItem(HINT_KEY);
            localStorage.removeItem('mp_shown_hints_v1');
            setActiveHint(null);
        };
    }, []);

    useEffect(() => {
        if (!hasResults || hintCheckedRef.current) return;
        const timer = setTimeout(() => {
            hintCheckedRef.current = true;
            const refFor = { follow: followBtnRef, bell_artist: bellBtnRef, share: shareBtnRef, track: firstTrackBtnRef };
            const seen = getSeenHints();
            const next = ARTIST_HINTS.find(h => !seen.includes(h) && refFor[h]?.current != null);
            if (next) {
                const autoShow = !getShownHints().includes(next);
                if (autoShow) markHintShown(next);
                setHintAutoShow(autoShow);
                setActiveHint(next);
            }
        }, 700);
        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasResults]);

    // Effective lists for the four searchable cards. Falls back to the
    // unfiltered display lists when no in-card filter query is active.
    const isMixesByFiltered = !!mixesByFiltered;
    const isMixesInclFiltered = !!mixesInclFiltered;
    const renderMixesByList = isMixesByFiltered
        ? (canAccess ? mixesByFiltered.mixes : mixesByFiltered.mixes.slice(0, FREE_RESULTS_LIMIT))
        : displayedMixesByArtist;
    const renderMixesInclList = isMixesInclFiltered
        ? (canAccess ? mixesInclFiltered.mixes : mixesInclFiltered.mixes.slice(0, FREE_RESULTS_LIMIT))
        : displayedMixesIncludingArtist;
    const renderTracksPlayed = tracksPlayedFiltered
        ? (canAccess ? tracksPlayedFiltered : tracksPlayedFiltered.slice(0, FREE_RESULTS_LIMIT))
        : tracksPlayed;
    const renderTracksOthers = tracksOthersFiltered
        ? (canAccess ? tracksOthersFiltered : tracksOthersFiltered.slice(0, FREE_RESULTS_LIMIT))
        : tracksPlayedByOthers;

    // DESIGN V2 — hero facts (the six stats as one quiet strip), the Play
    // handler (plays the top track through the row's own preview player via
    // the `djpal-row-play` event, so lazy resolution / queue context are the
    // row's) and the lists sized to the Overview tab.
    const v2Overview = v2 && v2Tab === 'overview';
    const v2Slice = (list) => (v2Overview ? list.slice(0, V2_OVERVIEW_ROWS) : list);
    // Tab mode: one full-width list, the inactive segment is not rendered.
    const v2TracksTab = v2 && v2Tab === 'tracks';
    const v2MixesTab = v2 && v2Tab === 'mixes';
    const v2Network = v2 && v2Tab === 'network';
    const v2ShowTracksPlayed = !v2TracksTab || v2TracksSeg === 'played';
    const v2ShowTracksOthers = !v2TracksTab || v2TracksSeg === 'by';
    const v2ShowMixesBy = !v2MixesTab || v2MixesSeg === 'by';
    const v2ShowMixesFeat = !v2MixesTab || v2MixesSeg === 'featuring';
    const byTitle = (a, b) => (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' });
    const v2SortTracks = (list) => !v2TracksTab || v2TracksSort === 'recent' ? list
        : v2TracksSort === 'plays' ? [...list].sort((a, b) => (b.mixCount || 0) - (a.mixCount || 0))
            : [...list].sort(byTitle);
    const v2SortMixes = (list) => !v2MixesTab || v2MixesSort === 'recent' ? list : [...list].sort(byTitle);
    // The two track lists from the server with real numbers (GET /v1/artists/:id/track-plays, 10-30): the
    // mixes-derived lists counted only the mixes loaded so far ("Simon Says" read 2 plays against 242, and
    // "Most played" sorted by those). Server-sorted and paged; an in-list filter, a v1 server (501) or a
    // failed call falls back to the mixes-derived list.
    const tpSort = v2TracksTab ? ({ recent: 'recent', plays: 'plays', alphabetical: 'alpha' }[v2TracksSort] || 'recent') : 'recent';
    const tpKey = v2 && artistPublicId ? [artistPublicId, tpSort, dateRange?.from || '', dateRange?.to || ''].join('|') : null;
    const tpRow = (r) => ({
        ...r,
        mixCount: r.playedCount,
        lastMix: r.lastPlay ? {
            publicId: r.lastPlay.mixPublicId, title: r.lastPlay.title || r.lastPlay.eventName, date: r.lastPlay.date,
            artist: Array.isArray(r.lastPlay.artists) ? r.lastPlay.artists.map((a) => a?.name || a).join(', ') : r.lastPlay.artists,
        } : null,
    });
    const fetchTp = useCallback(async (key, by, page) => {
        const token = localStorage.getItem('djpal:token');
        if (!token || !window.electron?.djpal?.artistTrackPlays) return { failed: true };
        const [publicId, sort, from, to] = key.split('|');
        const res = await window.electron.djpal.artistTrackPlays(token, { artistPublicId: publicId, by, sort, page, limit: 50, dateRange: (from || to) ? { from: from || null, to: to || null } : null }).catch(() => null);
        if (!res || res.error || res.unsupported || !Array.isArray(res.results)) return { failed: true };
        const pg = res.pagination || {};
        return { rows: res.results.map(tpRow), page, total: pg.totalCount ?? null, hasMore: page < (pg.totalPages || 0) };
    }, []);
    useEffect(() => {
        if (!tpKey) return undefined;
        let cancelled = false;
        setTp({ key: tpKey, self: { loading: true }, others: { loading: true } });
        ['self', 'others'].forEach((by) => fetchTp(tpKey, by, 1).then((r) => {
            if (!cancelled) setTp((prev) => (prev.key === tpKey ? { ...prev, [by]: r } : prev));
        }));
        return () => { cancelled = true; };
    }, [tpKey, fetchTp]);
    const tpMore = (by) => {
        const cur = tp.key === tpKey ? tp[by] : null;
        if (!cur?.rows || cur.loading || !cur.hasMore) return;
        setTp((prev) => ({ ...prev, [by]: { ...cur, loading: true } }));
        fetchTp(tpKey, by, cur.page + 1).then((r) => setTp((prev) => (prev.key !== tpKey ? prev
            : { ...prev, [by]: r.failed ? { ...cur, loading: false, hasMore: false } : { ...r, rows: [...cur.rows, ...r.rows] } })));
    };
    const tpList = (by, query) => {
        const cur = tp.key === tpKey && !query.trim() ? tp[by] : null;
        if (!cur || cur.failed) return null;
        return cur;
    };
    // The Network tab's two artist lists, a page at a time from GET /v1/artists/:id/supported (10-31):
    // the search sends only their top 50 (the whole list was ~14 s of a big DJ's first visit). Server
    // sort and name filter; a date-scoped page (full arrays), an old server or a failed call falls back
    // to the client-side list.
    const [spQuery, setSpQuery] = useState({ by: '', of: '' });
    useEffect(() => {
        const t = setTimeout(() => setSpQuery({ by: artistsTheySupportQuery.trim(), of: artistsSupportingThemQuery.trim() }), 250);
        return () => clearTimeout(t);
    }, [artistsTheySupportQuery, artistsSupportingThemQuery]);
    const spSort = { popularity: 'popularity', recent: 'recent', alphabetical: 'alpha' }[networkSort] || 'popularity';
    const spKey = v2Network && artistPublicId && artistResults?.supportPaged ? [artistPublicId, spSort, spQuery.by, spQuery.of].join('|') : null;
    const fetchSp = useCallback(async (key, direction, page) => {
        const token = localStorage.getItem('djpal:token');
        if (!token || !window.electron?.djpal?.artistSupported) return { failed: true };
        const [publicId, sort, qBy, qOf] = key.split('|');
        const res = await window.electron.djpal.artistSupported(token, { artistPublicId: publicId, direction, sort, q: direction === 'by' ? qBy : qOf, page, limit: 50 }).catch(() => null);
        if (!res || res.error || res.unsupported || !Array.isArray(res.results)) return { failed: true };
        const pg = res.pagination || {};
        return { rows: res.results, page, total: pg.totalCount ?? null, hasMore: page < (pg.totalPages || 0) };
    }, []);
    useEffect(() => {
        if (!spKey) return undefined;
        let cancelled = false;
        // Rows stay on screen while a new filter/sort loads — a skeleton per keystroke flickered.
        setSp((prev) => ({ key: spKey, by: { loading: true, rows: prev.by?.rows }, of: { loading: true, rows: prev.of?.rows } }));
        ['by', 'of'].forEach((d) => fetchSp(spKey, d, 1).then((r) => {
            if (!cancelled) setSp((prev) => (prev.key === spKey ? { ...prev, [d]: r } : prev));
        }));
        return () => { cancelled = true; };
    }, [spKey, fetchSp]);
    const spMore = (d) => {
        const cur = sp.key === spKey ? sp[d] : null;
        if (!cur?.rows || cur.loading || !cur.hasMore) return;
        setSp((prev) => ({ ...prev, [d]: { ...cur, loading: true } }));
        fetchSp(spKey, d, cur.page + 1).then((r) => setSp((prev) => (prev.key !== spKey ? prev
            : { ...prev, [d]: r.failed ? { ...cur, loading: false, hasMore: false } : { ...r, rows: [...cur.rows, ...r.rows] } })));
    };
    const spBy = spKey && sp.key === spKey && !sp.by?.failed ? sp.by : null;
    const spOf = spKey && sp.key === spKey && !sp.of?.failed ? sp.of : null;
    const spRows = (cur) => (cur.rows ? (canAccess ? cur.rows : cur.rows.slice(0, FREE_RESULTS_LIMIT)) : []);
    const networkSupportRows = spBy ? spRows(spBy) : sortArtists(displayedArtistsSupporting).slice(0, networkSupportLimit);
    const networkSupportersRows = spOf ? spRows(spOf) : sortArtists(displayedSupportingArtists).slice(0, networkSupportersLimit);
    const tpSelf = tpList('self', tracksByQuery);
    const tpOthers = tpList('others', tracksOthersQuery);
    const tpRows = (cur) => (cur.rows ? (canAccess ? cur.rows : cur.rows.slice(0, FREE_RESULTS_LIMIT)) : []);
    const tracksPlayedView = tpSelf ? tpRows(tpSelf) : v2SortTracks(renderTracksPlayed);
    const tracksOthersView = tpOthers ? tpRows(tpOthers) : v2SortTracks(renderTracksOthers);
    // A column still loading inside the loaded page: v2 = rows at the loaded
    // row's box (no slabs, no hairlines); classic keeps its skeleton.
    const tpSkeleton = v2
        ? <SkelRows count={3} art="square" lines={2} numbered rankW={22} trailing />
        : <div className="djpal-skel">{[0, 1, 2].map(k => (<div className="djpal-skel-track-row" key={k}><div className="djpal-skel-block skel-art" /><div className="skel-track-lines"><div className="djpal-skel-block skel-line" /><div className="djpal-skel-block skel-line short" /></div></div>))}</div>;
    // Network lists hold artists: round avatar, name only.
    const netSkeleton = v2 ? <SkelRows count={3} art="round" lines={1} /> : tpSkeleton;
    const mixesByView = v2SortMixes(renderMixesByList);
    const mixesInclView = v2SortMixes(renderMixesInclList);
    const TRACK_SORTS = ['recent', 'plays', 'alphabetical'];
    const TRACK_SORT_LABELS = { recent: 'Recent', plays: 'Most played', alphabetical: 'A–Z' };
    const MIX_SORTS = ['recent', 'alphabetical'];
    const MIX_SORT_LABELS = { recent: 'Recent', alphabetical: 'A–Z' };
    // Row context column (tab mode only, where the width exists): the latest
    // mix the track was heard in, as a link to that mix.
    const v2RowCtx = (track, withDj) => {
        const m = track.lastMix;
        if (!v2TracksTab || !m) return null;
        const dj = withDj ? (m.djName || m.artist) : null;
        const when = m.date ? relativeDate(m.date) : null;
        const text = [dj, m.title, when].filter(Boolean).join(' · ');
        return (
            <button type="button" className="v2-row-ctx" title={text} onClick={(e) => { e.stopPropagation(); handleMixClick(m); }}>
                {dj && <span className="v2-row-ctx-dj">{dj}</span>}{dj && m.title && ' · '}{m.title}{when && <span className="v2-faint">{(dj || m.title) && ' · '}{when}</span>}
            </button>
        );
    };
    const v2Performed = statsByArtist.totalAppearances ?? paginationByArtist?.totalCount ?? mixesByArtist.length;
    const v2Facts = (!v2 || noResults) ? [] : [
        statsIncluded.totalAppearances != null && { value: fmtNum(statsIncluded.totalAppearances), label: 'DJ plays' },
        statsIncluded.totalSupporting != null && { value: fmtNum(statsIncluded.totalSupporting), label: 'Supporting DJs' },
        statsIncluded.totalTracksSupported != null && { value: fmtNum(statsIncluded.totalTracksSupported), label: 'Tracks with support' },
        statsIncluded.mixesIncluded != null && { value: fmtNum(statsIncluded.mixesIncluded), label: 'Mixes featuring them' },
        (statsByArtist.totalAppearances > 0 || mixesByArtist.length > 0) && { value: fmtNum(v2Performed), label: 'Mixes performed' },
        (statsByArtist.totalTracksPlayed > 0 || tracksPlayed.length > 0) && { value: fmtNum(statsByArtist.totalTracksPlayed ?? tracksPlayed.length), label: 'Tracks played' },
    ].filter(Boolean);
    const v2PlayId = tracksPlayedAll.length ? 'played-0' : (tracksPlayedByOthersAll.length ? 'others-0' : null);
    const handleV2Play = () => {
        if (!v2PlayId) return;
        if (v2Tab !== 'overview' && v2Tab !== 'tracks') setV2Tab('overview');
        setTimeout(() => window.dispatchEvent(new CustomEvent('djpal-row-play', { detail: v2PlayId })), 0);
    };
    const v2Name = artist?.name || artistResults?.artistName || '';
    const v2Genre = artist?.styles?.length > 0 ? artist.styles.slice(0, 3).join(', ') : '';
    const v2Verified = supportingArtists.length > 0;
    const v2SupportedBy = supportingArtists.length > 0 ? (
        <span>
            <span className="supported-grey">Supported by </span>
            {supportingArtists.slice(0, 2).map((a, i) => (
                <React.Fragment key={entityId(a) || a.name}>
                    {i > 0 && <span className="supported-grey">, </span>}
                    {artistHoverWrap(a, (
                        <span className="supported-name" onClick={() => handleArtistClick(a.name, entityId(a))}>{a.name}</span>
                    ))}
                </React.Fragment>
            ))}
            {supportingArtistsTotal > 2 && (
                <>
                    <span className="supported-grey"> and </span>
                    <span className="supported-others" onClick={() => setV2Tab('network')}>{fmtNum(supportingArtistsTotal - 2)} others</span>
                </>
            )}
        </span>
    ) : null;

    // DESIGN V2: the date range filters the stats and lists, so it lives on the tabs row.
    const v2DateControls = v2 ? (
        <div className="v2-tabs-tools">
                                    <div className="v2-hero-more" ref={datePopoverRef}>
                                        <button
                                            ref={dateRangeBtnRef}
                                            type="button"
                                            className={`v2-btn icon${dateRange ? ' on' : ''}`}
                                            title={dateRange ? `Date range: ${dateRange.from || '…'} – ${dateRange.to || '…'}` : 'Date range'}
                                            aria-label="More — date range"
                                            onClick={() => setShowDateModal((v) => !v)}
                                        >
                                            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" /></svg>
                                        </button>
                                        {showDateModal && !isNarrow && (
                                            <div className="artist-header-date-popover">
                                                <DJPalDateRangeModal
                                                    dateRange={dateRange}
                                                    onApply={handleDateApply}
                                                    onClose={() => setShowDateModal(false)}
                                                />
                                            </div>
                                        )}
                                        {showDateModal && isNarrow && (
                                            <DateRangeSheet
                                                dateRange={dateRange}
                                                onApply={handleDateApply}
                                                onClose={() => setShowDateModal(false)}
                                            />
                                        )}
                                    </div>
                                    {dateRange && (
                                        <button type="button" className="v2-chip sm on" title="Clear date range" onClick={() => handleDateApply(null)}>
                                            {dateRange.from || '…'} – {dateRange.to || '…'} <small>×</small>
                                        </button>
                                    )}
        </div>
    ) : null;
    return (
        <div className={`djpal-artist-search${v2 ? ' v2-ambient v2-entity-page' : ''}`}>
            {/* Bar only for refinements (date-range re-query) where the old
                page stays on screen; fresh loads get the skeleton below. */}
            <DJPalProgressBar visible={isSearching && refining && !!artistResults} />

            {(() => {
                const errorBanner = searchError && (
                    <ErrorBanner
                        error={searchError}
                        surface="mixpedia_search"
                        onRetry={() => {
                            const name = pendingArtist?.name || artistResults?.artistName || artistQuery;
                            if (!name) return;
                            dispatch(searchArtist({
                                artistName: name,
                                artistId: entityId(pendingArtist) || entityId(artistResults?.artist) || null,
                                dateRange,
                            }));
                        }}
                    />
                );
                const pendingName = pendingArtist?.name || artistQuery || '';
                // No page yet: the pending header stays (name + photo) and the
                // skeleton below it turns into the error + Retry on failure.
                if (artistResults || !pendingName || !(isSearching || searchError)) return errorBanner || null;
                // DESIGN V2: the loaded artist page with its content blanked —
                // round hero, six facts, Overview / Tracks / Mixes / Network,
                // then the two ranked track columns (see v2/Skeleton). On a
                // failed load the hero stays and the error replaces the body.
                if (v2 && !errorBanner) return (
                    <div className="djpal-results">
                        <EntityPageSkeleton
                            heroClassName="v2-artist-hero"
                            round
                            art={safeArtwork(pendingArtist?.artworkUrl) ? pendingArtist.artworkUrl : null}
                            title={pendingName}
                            actions={{ primaryW: 93, pills: [112], circles: 2 }}
                            facts={6}
                            tabs={[64, 46, 42, 58]}
                            columns={[
                                { art: 'square', lines: 2, numbered: true, trailing: true, link: true, titleW: 168, count: 6, rankW: 22 },
                                { art: 'square', lines: 2, numbered: true, trailing: true, link: true, titleW: 250, count: 6, rankW: 22 },
                            ]}
                        />
                    </div>
                );
                return (
                    <div className="djpal-results djpal-artist-loading">
                        <div className="djpal-artist-header">
                            {safeArtwork(pendingArtist?.artworkUrl) ? (
                                // v2: the hero's own variant (coverAt 400) so the picture is cached when the hero mounts
                                // and paints without its fade; a smaller variant here made the avatar blink on load
                                v2 ? <img className="artist-avatar-lg" src={coverAt(pendingArtist.artworkUrl, 400)} alt={pendingName} />
                                   : <CoverArt className="artist-avatar-lg" src={pendingArtist.artworkUrl} size={96} eager alt={pendingName} />
                            ) : (
                                // Unknown artwork while loading → quiet skeleton (the
                                // gradient-initial avatar is the legitimate "artist has
                                // no photo" END state; flashing it first read as a bug).
                                <div className="artist-avatar-lg avatar-pending" />
                            )}
                            <div className="artist-info">
                                <div className="artist-name-lg">{pendingName}</div>
                                {v2 && !searchError && <><span className="v2-skel-line" /><span className="v2-skel-line sm" /><span className="v2-skel-pill" /></>}
                            </div>
                        </div>
                        {errorBanner || (
                        <div className="djpal-skel">
                            {/* 6 placeholders = the real page's 6 stat tiles
                                (mobile's 3-col grid renders two clean rows). */}
                            <div className="djpal-stats-grid">
                                {[0, 1, 2, 3, 4, 5].map(i => (
                                    <div className="stat-item" key={i}>
                                        <div className="djpal-skel-block skel-stat-value" />
                                        <div className="djpal-skel-block skel-stat-label" />
                                    </div>
                                ))}
                            </div>
                            <div className="djpal-two-col">
                                {[0, 1].map(c => (
                                    <div className="djpal-card" key={c}>
                                        <div className="djpal-skel-block skel-card-title" />
                                        {[0, 1, 2, 3].map(r => (
                                            <div className="djpal-skel-track-row" key={r}>
                                                <div className="djpal-skel-block skel-art" />
                                                <div className="skel-track-lines">
                                                    <div className="djpal-skel-block skel-line" />
                                                    <div className="djpal-skel-block skel-line short" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                        )}
                    </div>
                );
            })()}

            {/* Empty outcome keeps the SAME header geometry as the pending
                view (avatar-lg + name), with the message + CTA in a bounded
                card where the first results card would render — the search
                resolves in place instead of swapping to a centered card. */}
            {/* TRUE not-found only — a resolved artist renders the full page
                below (with a notice when they have no data yet). */}
            {noResults && !artist && (
                <div className="djpal-results">
                    <div className="djpal-artist-header">
                        {safeArtwork(artist?.artworkUrl) ? (
                            <CoverArt className="artist-avatar-lg" src={artist.artworkUrl} covers={artist.covers} size={96} eager alt={artist?.name || artistResults.artistName}
                                fallback={<div className="artist-avatar-lg">{(artist?.name || artistResults.artistName || '?').charAt(0).toUpperCase()}</div>} />
                        ) : (
                            <div className="artist-avatar-lg">
                                {(artist?.name || artistResults.artistName || '?').charAt(0).toUpperCase()}
                            </div>
                        )}
                        <div className="artist-info">
                            <div className="artist-name-lg">
                                {artist?.name || artistResults.artistName}
                            </div>
                        </div>
                    </div>
                    <div className="djpal-empty-body">
                    {noResultsInRange ? (
                        <>
                            <div className="djpal-artist-empty-message">
                                No mixes or supported tracks in the selected date range.
                            </div>
                            <div className="djpal-artist-empty-cta">
                                <button className="djpal-empty-clear-range-btn" onClick={() => handleDateApply(null)}>
                                    Clear date range
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="djpal-artist-empty-message">
                                We haven&apos;t found mixes or supported tracks for this artist yet.
                            </div>
                            <div className="djpal-artist-empty-cta">
                                <FollowArtistButton
                                    artist={{
                                        name: artist?.name || artistResults.artistName,
                                        artworkUrl: artist?.artworkUrl,
                                    }}
                                />
                                <NotifyBellButton
                                    entityType="artist"
                                    entity={{ name: artist?.name || artistResults.artistName }}
                                    from="artist_empty_state"
                                    nudge
                                />
                                <span className="djpal-artist-empty-cta-hint">
                                    Follow them, then turn on the bell — we&apos;ll tell you the moment we find something.
                                </span>
                            </div>
                        </>
                    )}
                    </div>
                </div>
            )}

            {(() => {
                const hintDefs = {
                    follow:      { targetRef: followBtnRef,     title: 'Follow this artist',        message: 'Save them to your artists and personalise your feed.' },
                    bell_artist: { targetRef: bellBtnRef,       title: 'Turn on the bell',          message: 'Following saves them to your feed — the bell is what notifies you. Turn it on to hear about new mixes and played tracks.' },
                    share:       { targetRef: shareBtnRef,      title: 'Share the impact',          message: "Export a shareable stats card showing this artist's full DJ support data." },
                    track:       { targetRef: firstTrackBtnRef, title: 'Dig deeper into any track', message: 'Click a track to find every DJ who played it.' },
                };
                const cfg = activeHint ? hintDefs[activeHint] : null;
                return cfg?.targetRef?.current ? (
                    <MixpediaHint
                        targetRef={cfg.targetRef}
                        title={cfg.title}
                        message={cfg.message}
                        autoShow={hintAutoShow}
                        onDismiss={() => {
                            markHintSeen(activeHint);
                            if (activeHint === 'share') markHintShown('share_track');
                            setActiveHint(null);
                        }}
                    />
                ) : null;
            })()}

            {(hasResults || (noResults && artist)) && (
                <div className="djpal-results" ref={resultsRef}>
                    {playsFor && <TrackMixAppearancesModal track={playsFor.track} subjectArtist={playsFor.subjectArtist} excludeArtist={playsFor.excludeArtist} onClose={() => setPlaysFor(null)} />}
                    {v2 ? (
                        <HeroHeader
                            className="v2-artist-hero"
                            round
                            art={safeArtwork(artist?.artworkUrl) ? artist.artworkUrl : <div className="v2-hero-letter">{v2Name.charAt(0).toUpperCase()}</div>}
                            covers={artist?.covers}
                            banner={artist?.heroCover?.url || null}
                            bannerFocus={artist?.heroCover?.focus || null}
                            artAlt={v2Name}
                            ambientKey={artistPublicId || v2Name}
                            kicker={null}
                            title={v2Name}
                            meta={[
                                v2Genre || null,
                                artist?.country || null,
                                statsIncluded?.mixesIncluded > 0
                                    ? <>Featured in <b className="supported-others" onClick={() => setV2Tab('mixes')}>{fmtNum(statsIncluded.mixesIncluded)}</b> mixes</>
                                    : (v2Performed > 0 ? <>Performed <b className="supported-others" onClick={() => setV2Tab('mixes')}>{fmtNum(v2Performed)}</b> mixes</> : null),
                            ]}
                            lines={(artist?.members?.length || artist?.memberOf?.length) ? <>
                                {renderMemberLine('Members', artist?.members)}
                                {renderMemberLine('Member of', artist?.memberOf)}
                            </> : null}
                            sub={v2SupportedBy}
                            subIcon={v2SupportedBy ? <SupportedCheck /> : null}
                            facts={v2Facts}
                            actions={(
                                <>
                                    <HeroPlayButton previewId={v2PlayId} onClick={handleV2Play} disabled={!v2PlayId} title={v2PlayId ? 'Play their top track' : 'Nothing to play yet'} />
                                    <span className="like-bell-group v2-follow-group">
                                        <FollowArtistButton ref={followBtnRef} prominent artist={{ name: v2Name, artworkUrl: artist?.artworkUrl }} />
                                        <NotifyBellButton ref={bellBtnRef} entityType="artist" entity={{ name: v2Name, artworkUrl: artist?.artworkUrl }} from="artist_header" nudge />
                                    </span>
                                    <ShareMenu onShareImage={handleExport} onSharePage={handleSharePage} onCopyLink={handleCopyLink} sharing={exporting} shareRef={shareBtnRef} quiet />
                                    {hasExternalLinks && (
                                        <div className="v2-hero-links">
                                            <ArtistLinksRow externalIds={artist?.externalIds} artistName={v2Name} event="artist_link_opened" />
                                        </div>
                                    )}
                                </>
                            )}
                        />
                    ) : (<>
                    {/* Artist Header */}
                    <div className="djpal-artist-header">
                        {/* Two rows: share+follow on top, the date filter on a
                            second row beneath them (mobile lands it level with
                            the byline). On desktop the rows are display:contents,
                            so this is the same single inline cluster as always. */}
                        <div className="artist-header-actions">
                            <div className="header-actions-row">
                                <ShareMenu onShareImage={handleExport} onSharePage={handleSharePage} onCopyLink={handleCopyLink} sharing={exporting} shareRef={shareBtnRef} quiet />
                                <span className="like-bell-group">
                                    <FollowArtistButton ref={followBtnRef} prominent artist={{ name: artist?.name || artistResults.artistName, artworkUrl: artist?.artworkUrl }} />
                                    <NotifyBellButton ref={bellBtnRef} entityType="artist" entity={{ name: artist?.name || artistResults.artistName, artworkUrl: artist?.artworkUrl }} from="artist_header" nudge />
                                </span>
                            </div>
                            <div className="header-actions-row">
                                {/* Mobile: the date filter collapses to an icon-only
                                    calendar button — cyan when a range is active,
                                    like Follow. */}
                                {isNarrow && (
                                    <button
                                        ref={dateRangeBtnRef}
                                        className={`date-range-btn icon-only ${dateRange ? 'active' : ''}`}
                                        title="Date range"
                                        aria-label="Date range"
                                        onClick={() => setShowDateModal((v) => !v)}
                                    >
                                        <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                            <line x1="16" y1="2" x2="16" y2="6" />
                                            <line x1="8" y1="2" x2="8" y2="6" />
                                            <line x1="3" y1="10" x2="21" y2="10" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>
                        {!isNarrow && (
                            <div className="artist-header-date-range" ref={datePopoverRef}>
                                <button
                                    ref={dateRangeBtnRef}
                                    className={`date-range-btn ${dateRange ? 'active' : ''}`}
                                    onClick={() => setShowDateModal((v) => !v)}
                                >
                                    <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                        <line x1="16" y1="2" x2="16" y2="6" />
                                        <line x1="8" y1="2" x2="8" y2="6" />
                                        <line x1="3" y1="10" x2="21" y2="10" />
                                    </svg>
                                    {dateRange ? `${dateRange.from || '...'} – ${dateRange.to || '...'}` : 'Date Range'}
                                </button>
                                {showDateModal && (
                                    <div className="artist-header-date-popover">
                                        <DJPalDateRangeModal
                                            dateRange={dateRange}
                                            onApply={handleDateApply}
                                            onClose={() => setShowDateModal(false)}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        {isNarrow && showDateModal && (
                            <DateRangeSheet
                                dateRange={dateRange}
                                onApply={handleDateApply}
                                onClose={() => setShowDateModal(false)}
                            />
                        )}
                        {safeArtwork(artist?.artworkUrl) ? (
                            <CoverArt className="artist-avatar-lg" src={artist.artworkUrl} covers={artist.covers} size={96} eager alt={artist.name}
                                fallback={<div className="artist-avatar-lg">{(artist?.name || artistResults.artistName).charAt(0).toUpperCase()}</div>} />
                        ) : (
                            <div className="artist-avatar-lg">
                                {(artist?.name || artistResults.artistName).charAt(0).toUpperCase()}
                            </div>
                        )}
                        <div className="artist-info">
                            <div className="artist-name-lg">
                                {artist?.name || artistResults.artistName}
                            </div>
                            {/* Genre line with the country as a smaller, muted
                                tail: "Electronic · United Kingdom" (country
                                alone when there is no genre). */}
                            {(artist?.styles?.length > 0 || artist?.country) && (
                                <div className="artist-byline">
                                    {artist?.styles?.length > 0 && artist.styles.slice(0, 3).join(', ')}
                                    {artist?.styles?.length > 0 && artist?.country && <span className="artist-byline-country"> · </span>}
                                    {artist?.country && <span className="artist-byline-country" title="Country">{artist.country}</span>}
                                </div>
                            )}
                            {renderMemberLine('Members', artist?.members)}
                            {renderMemberLine('Member of', artist?.memberOf)}
                            {(() => {
                                // Primary: how many other DJs' mixes feature their tracks (the
                                // most universally meaningful stat — producers + DJs both have it).
                                // Fallback for artists with zero features: show their own
                                // performance count instead. Both link to the relevant card below.
                                const featured = statsIncluded?.mixesIncluded ?? 0;
                                const performed = statsByArtist?.totalAppearances ?? paginationByArtist?.totalCount ?? mixesByArtist?.length ?? 0;
                                if (featured > 0) {
                                    return (
                                        <div className="artist-genre">
                                            Featured in <span className="supported-others" onClick={handleFeaturingClick}>{featured.toLocaleString()} mixes</span>
                                        </div>
                                    );
                                }
                                if (performed > 0) {
                                    return (
                                        <div className="artist-genre">
                                            Performed <span className="supported-others" onClick={handleMixesByClick}>{performed.toLocaleString()} mixes</span>
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                        </div>
                    </div>
                    {/* "Supported by …" line; the socials icon row (real brand
                        glyphs) sits at its right end, centred on the text. */}
                    {(supportingArtists.length > 0 || hasExternalLinks) && (
                        <div className="artist-supported-line artist-supported-line--below">
                            {supportingArtists.length > 0 && (<>
                            <SupportedCheck />
                            <span>
                                <span className="supported-grey">Supported by </span>
                                {supportingArtists.slice(0, 2).map((a, i) => (
                                    <React.Fragment key={entityId(a) || a.name}>
                                        {i > 0 && <span className="supported-grey">, </span>}
                                        {artistHoverWrap(a, (
                                            <span className="supported-name" onClick={() => handleArtistClick(a.name, entityId(a))}>{a.name}</span>
                                        ))}
                                    </React.Fragment>
                                ))}
                                {supportingArtistsTotal > 2 && (
                                    <>
                                        <span className="supported-grey"> and </span>
                                        <span className="supported-others" onClick={handleOthersClick}>{supportingArtistsTotal - 2} others</span>
                                    </>
                                )}
                            </span>
                            </>)}
                            <ArtistLinksRow externalIds={artist?.externalIds} artistName={artist?.name || artistResults.artistName} event="artist_link_opened" />
                        </div>
                    )}
                    </>)}

                    {/* No data yet: keep the full page (header actions) and
                        swap stats + data boxes for a quiet notice — the
                        header's Follow is the notify CTA. */}
                    {noResults && (
                        <div className="djpal-empty-body">
                            <div className="djpal-artist-empty-message">
                                {noResultsInRange
                                    ? 'No mixes or supported tracks in the selected date range.'
                                    : "We haven't found mixes or supported tracks for this artist yet."}
                            </div>
                            {noResultsInRange ? (
                                <button className="djpal-empty-clear-range-btn" onClick={() => handleDateApply(null)}>
                                    Clear date range
                                </button>
                            ) : (
                                <span className="djpal-artist-empty-cta-hint with-icon">
                                    {/* Same glyph as the header's Follow pill — the
                                        icon links this line to the control it
                                        points at. */}
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                                        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                                    </svg>
                                    Follow them, then turn on the bell — we&apos;ll tell you the moment we find something.
                                </span>
                            )}
                        </div>
                    )}

                    {!noResults && (<>
                    {v2 ? (
                        <div className="v2-tabs-row"><PageTabs className="v2-entity-tabs" items={V2_TABS} value={v2Tab} onChange={setV2Tab} />{v2DateControls}</div>
                    ) : (
                    <div className="djpal-stats-grid">
                        {statsIncluded.totalTracksSupported != null && (
                            <div className="stat-item">
                                <div className="stat-value">{statsIncluded.totalTracksSupported}</div>
                                <div className="stat-label">Tracks with DJ Support</div>
                            </div>
                        )}
                        {statsIncluded.totalSupporting != null && (
                            <div className="stat-item">
                                <div className="stat-value">{statsIncluded.totalSupporting}</div>
                                <div className="stat-label">Supporting DJs</div>
                            </div>
                        )}
                        {statsIncluded.totalAppearances != null && (
                            <div className="stat-item">
                                <div className="stat-value">{statsIncluded.totalAppearances}</div>
                                <div className="stat-label">Total DJ Plays</div>
                            </div>
                        )}
                        {statsIncluded.mixesIncluded != null && (
                            <div className="stat-item">
                                <div className="stat-value">{statsIncluded.mixesIncluded}</div>
                                <div className="stat-label">Mixes Featuring Them</div>
                            </div>
                        )}
                        {(statsByArtist.totalAppearances > 0 || mixesByArtist.length > 0) && (
                            <div className="stat-item">
                                <div className="stat-value">{statsByArtist.totalAppearances ?? paginationByArtist?.totalCount ?? mixesByArtist.length}</div>
                                <div className="stat-label">Mixes Performed</div>
                            </div>
                        )}
                        {(statsByArtist.totalTracksPlayed > 0 || tracksPlayed.length > 0) && (
                            <div className="stat-item">
                                <div className="stat-value">{statsByArtist.totalTracksPlayed ?? tracksPlayed.length}</div>
                                <div className="stat-label">Tracks Played</div>
                            </div>
                        )}
                    </div>
                    )}

                    {/* Track Relationships */}
                    {(!v2 || v2Tab === 'overview' || v2Tab === 'tracks') && (<>
                    {v2TracksTab && (
                        <ListToolbar
                            segments={[
                                { key: 'played', label: 'They played', count: tpSelf?.total ?? (statsByArtist.totalTracksPlayed || tracksPlayedAll.length) },
                                { key: 'by', label: 'Their tracks played by others', count: tpOthers?.total ?? (statsIncluded.totalTracksSupported || tracksPlayedByOthersAll.length) },
                            ]}
                            value={v2TracksSeg}
                            onChange={setV2TracksSeg}
                        >
                            {(v2TracksSeg === 'played' ? tracksPlayedView : tracksOthersView).length > 1 && <SortChips value={v2TracksSort} onChange={setV2TracksSort} modes={TRACK_SORTS} labels={TRACK_SORT_LABELS} />}
                            {v2TracksSeg === 'played' && tracksPlayedAll.length > 0 && <CardSearch value={tracksByQuery} onChange={setTracksByQuery} placeholder="Filter tracks…" ariaLabel="Filter tracks they played" />}
                            {v2TracksSeg === 'by' && tracksPlayedByOthersAll.length > 0 && <CardSearch value={tracksOthersQuery} onChange={setTracksOthersQuery} placeholder="Filter tracks…" ariaLabel="Filter tracks played by others" />}
                            {(v2TracksSeg === 'played' ? tracksPlayedView : tracksOthersView).length > 0 && <PlayAllButton tracks={v2TracksSeg === 'played' ? tracksPlayedView : tracksOthersView} from="artist_tracks" />}
                        </ListToolbar>
                    )}
                    <div className={`djpal-two-col${v2TracksTab ? ' v2-single' : ''}`}>
                        {v2ShowTracksPlayed && <div className="djpal-card">
                            {v2 ? (v2Overview &&
                                <SectionHeader title="Tracks they played" link={tracksPlayedAll.length > 0 ? `See all${statsByArtist.totalTracksPlayed ? ` ${fmtNum(statsByArtist.totalTracksPlayed)}` : ''}` : null} onLink={() => v2OpenTracks('played')} />
                            ) : (
                            <div className="djpal-card-header">
                                <h3>Tracks they played</h3>
                                {tracksPlayedAll.length > 0 && (
                                    <div className="djpal-card-header-actions">
                                        <CardSearch
                                            value={tracksByQuery}
                                            onChange={setTracksByQuery}
                                            placeholder="Filter tracks…"
                                            ariaLabel="Filter tracks they played"
                                        />
                                        <button className="djpal-card-expand-btn" title="Open list view" onClick={handleOpenTracksPlayedModal}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                                        </button>
                                        <button className="djpal-card-expand-btn" title="Show all" onClick={handleShowAllTracksPlayed}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                            )}
                            {(tpSelf ? (tpSelf.rows ? tpSelf.rows.length > 0 : true) : tracksPlayedAll.length > 0) ? (
                                <>
                                <DJPalScrollableList
                                    maxHeight="300px"
                                    onScrollBottom={canAccess && !v2 ? (tracksByQuery.trim() ? handleLoadMoreTracksByFiltered : handleLoadMoreTracksPlayed) : undefined}
                                >
                                    {tpSelf && !tpSelf.rows && tpSkeleton}
                                    {renderTracksPlayed.length === 0 && tracksByQuery.trim() && (
                                        tracksBySearching ? (
                                            <div className="djpal-skel">{[0, 1, 2].map(k => (<div className="djpal-skel-track-row" key={k}><div className="djpal-skel-block skel-art" /><div className="skel-track-lines"><div className="djpal-skel-block skel-line" /><div className="djpal-skel-block skel-line short" /></div></div>))}</div>
                                        ) : (
                                            <div className="djpal-empty-hint">No matches found.</div>
                                        )
                                    )}
                                    {v2Slice(tracksPlayedView).map((track, i) => {
                                        const art = track.artwork || track.artworkUrl || track.image;
                                        return (
                                            <div className="djpal-track-row clickable" key={`played-${i}`} ref={i === 0 ? firstTrackBtnRef : null} onContextMenu={openRowCtx(`played-${i}`)} {...delayedClick(() => handleSearchTrack({ stopPropagation: () => {} }, track))}>
                                                {v2 && <span className="v2-row-rank">{i + 1}</span>}
                                                <DJPalAudioPreview src={track.previewUrl || track.sampleUrl || track.preview_url || track.preview} youtubeId={track.youtubeId} soundcloudUrl={track.soundcloudUrl} trackId={track.id} durationSec={track.durationSec ?? track.duration} pageUrl={track.pageUrl} previewId={`played-${i}`} trackName={track.title} trackArtist={track.artist} trackCover={art} playContext={{ items: tracksPlayedView, index: i, type: 'tracks' }} playbackBlocked={track.playbackBlocked}>
                                                    <div className="track-artwork">
                                                        {art ? <CoverArt src={art} covers={track.covers} size={48} fallback={<div className="artwork-placeholder">&#9835;</div>} /> : <div className="artwork-placeholder">&#9835;</div>}
                                                    </div>
                                                </DJPalAudioPreview>
                                                <div className="track-info">
                                                    <span className="track-label" title={track.artist ? `${track.artist} – ${track.title}` : track.title}>{hl(track.title)}</span>
                                                    {(track.artist || (track.artists && track.artists.length > 0)) && (
                                                        <span className="track-sublabel track-artist-line">
                                                            <MultiArtistText
                                                                artists={track.artists}
                                                                fallbackText={track.artist}
                                                                segmentClassName="clickable-artist"
                                                                onSelect={(a) => handleArtistClick(a.name, entityId(a), a.artworkUrl)}
                                                            />
                                                        </span>
                                                    )}
                                                </div>
                                                {v2RowCtx(track, false)}
                                                {v2 && rowCount(track, 'mixes') && (!v2TracksTab || track.mixCount > 1) && <button type="button" className="v2-row-meta v2-row-count-btn" title="Show the mixes" onClick={(e) => openPlays(e, track, 'played')}>{rowCount(track, 'mixes')}</button>}
                                                <LikeButton track={track} />
                                                <TrackContextMenu
                                                    track={track}
                                                    onSearch={handleSearchTrack}
                                                    mixAppearances={artist ? {
                                                        mode: 'byArtist',
                                                        subjectArtist: { name: artist.name, id: entityId(artist) },
                                                    } : undefined}
                                                    desktopLike
                                                    openAtPoint={rowCtxFor(`played-${i}`)}
                                                />

                                            </div>
                                        );
                                    })}
                                    {(loadingMoreTracksBy || loadingMoreTracksByFiltered) && !(v2 && v2Tab === 'tracks' && canAccess) && <div className="djpal-load-more-spinner"><span className="mix-play-spinner" /></div>}
                                </DJPalScrollableList>
                                {/* v2: AutoLoad draws the one load-more spinner; the list's own spinner above stays for classic (Erez 10-30: two spinners) */}
                                {v2 && v2Tab === 'tracks' && canAccess && (
                                    (tpSelf ? <AutoLoad onMore={() => tpMore('self')} loading={!!(tpSelf.rows && tpSelf.loading)} hasMore={!!tpSelf.hasMore} />
                                    : <AutoLoad onMore={tracksByQuery.trim() ? handleLoadMoreTracksByFiltered : handleLoadMoreTracksPlayed} loading={loadingMoreTracksBy || loadingMoreTracksByFiltered} hasMore={tracksPlayedLimit < tracksPlayedAll.length || !!paginationByArtist?.hasNext} />)
                                )}
                                {!canAccess && tracksPlayedAll.length > FREE_RESULTS_LIMIT && (
                                    <button className="djpal-tier-lock-btn" onClick={() => dispatch(showUpgradeModal('full-mixpedia'))}>
                                        <svg className="lock-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                                        + Unlock {tracksPlayedAll.length - FREE_RESULTS_LIMIT} more tracks
                                    </button>
                                )}
                                </>
                            ) : (
                                <div className="djpal-empty-hint">No tracklist data available yet</div>
                            )}
                        </div>}
                        {v2ShowTracksOthers && <div className="djpal-card">
                            {v2 ? (v2Overview &&
                                <SectionHeader title="Their tracks played by others" link={tracksPlayedByOthersAll.length > 0 ? `See all${statsIncluded.totalTracksSupported ? ` ${fmtNum(statsIncluded.totalTracksSupported)}` : ''}` : null} onLink={() => v2OpenTracks('by')} />
                            ) : (
                            <div className="djpal-card-header">
                                <h3>Their tracks played by others</h3>
                                {tracksPlayedByOthersAll.length > 0 && (
                                    <div className="djpal-card-header-actions">
                                        <CardSearch
                                            value={tracksOthersQuery}
                                            onChange={setTracksOthersQuery}
                                            placeholder="Filter tracks…"
                                            ariaLabel="Filter tracks played by others"
                                        />
                                        <button className="djpal-card-expand-btn" title="Open list view" onClick={handleOpenTracksByOthersModal}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                                        </button>
                                        <button className="djpal-card-expand-btn" title="Show all" onClick={handleShowAllTracksByOthers}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                            )}
                            {(tpOthers ? (tpOthers.rows ? tpOthers.rows.length > 0 : true) : tracksPlayedByOthersAll.length > 0) ? (
                                <>
                                <DJPalScrollableList
                                    maxHeight="300px"
                                    onScrollBottom={canAccess && !v2 ? (tracksOthersQuery.trim() ? handleLoadMoreTracksOthersFiltered : handleLoadMoreTracksOthers) : undefined}
                                >
                                    {tpOthers && !tpOthers.rows && tpSkeleton}
                                    {renderTracksOthers.length === 0 && tracksOthersQuery.trim() && (
                                        tracksOthersSearching ? (
                                            <div className="djpal-skel">{[0, 1, 2].map(k => (<div className="djpal-skel-track-row" key={k}><div className="djpal-skel-block skel-art" /><div className="skel-track-lines"><div className="djpal-skel-block skel-line" /><div className="djpal-skel-block skel-line short" /></div></div>))}</div>
                                        ) : (
                                            <div className="djpal-empty-hint">No matches found.</div>
                                        )
                                    )}
                                    {v2Slice(tracksOthersView).map((track, i) => {
                                        const art = track.artwork || track.artworkUrl || track.image;
                                        return (
                                            <div className="djpal-track-row clickable" key={`others-${i}`} onContextMenu={openRowCtx(`others-${i}`)} {...delayedClick(() => handleSearchTrack({ stopPropagation: () => {} }, track))}>
                                                {v2 && <span className="v2-row-rank">{i + 1}</span>}
                                                <DJPalAudioPreview src={track.previewUrl || track.sampleUrl || track.preview_url || track.preview} youtubeId={track.youtubeId} soundcloudUrl={track.soundcloudUrl} trackId={track.id} durationSec={track.durationSec ?? track.duration} pageUrl={track.pageUrl} previewId={`others-${i}`} trackName={track.title} trackArtist={track.artist} trackCover={art} playContext={{ items: tracksOthersView, index: i, type: 'tracks' }} playbackBlocked={track.playbackBlocked}>
                                                    <div className="track-artwork">
                                                        {art ? <CoverArt src={art} covers={track.covers} size={48} fallback={<div className="artwork-placeholder">&#9835;</div>} /> : <div className="artwork-placeholder">&#9835;</div>}
                                                    </div>
                                                </DJPalAudioPreview>
                                                <div className="track-info">
                                                    <span className="track-label" title={track.artist ? `${track.artist} – ${track.title}` : track.title}>{hl(track.title)}</span>
                                                    {(track.artist || (track.artists && track.artists.length > 0)) && (
                                                        <span className="track-sublabel track-artist-line">
                                                            <MultiArtistText
                                                                artists={track.artists}
                                                                fallbackText={track.artist}
                                                                segmentClassName="clickable-artist"
                                                                onSelect={(a) => handleArtistClick(a.name, entityId(a), a.artworkUrl)}
                                                            />
                                                        </span>
                                                    )}
                                                </div>
                                                {v2RowCtx(track, true)}
                                                {v2 && rowCount(track, 'plays') && (!v2TracksTab || track.mixCount > 1) && <button type="button" className="v2-row-meta v2-row-count-btn" title="Show the mixes" onClick={(e) => openPlays(e, track, 'others')}>{rowCount(track, 'plays')}</button>}
                                                <LikeButton track={track} />
                                                <TrackContextMenu
                                                    track={track}
                                                    onSearch={handleSearchTrack}
                                                    mixAppearances={{ mode: 'all' }}
                                                    desktopLike
                                                    openAtPoint={rowCtxFor(`others-${i}`)}
                                                />

                                            </div>
                                        );
                                    })}
                                    {(loadingMoreTracksIncl || loadingMoreTracksOthersFiltered) && !(v2 && v2Tab === 'tracks' && canAccess) && <div className="djpal-load-more-spinner"><span className="mix-play-spinner" /></div>}
                                </DJPalScrollableList>
                                {v2 && v2Tab === 'tracks' && canAccess && (
                                    (tpOthers ? <AutoLoad onMore={() => tpMore('others')} loading={!!(tpOthers.rows && tpOthers.loading)} hasMore={!!tpOthers.hasMore} />
                                    : <AutoLoad onMore={tracksOthersQuery.trim() ? handleLoadMoreTracksOthersFiltered : handleLoadMoreTracksOthers} loading={loadingMoreTracksIncl || loadingMoreTracksOthersFiltered} hasMore={tracksOthersLimit < tracksPlayedByOthersAll.length || !!paginationIncluding?.hasNext} />)
                                )}
                                {!canAccess && tracksPlayedByOthersAll.length > FREE_RESULTS_LIMIT && (
                                    <button className="djpal-tier-lock-btn" onClick={() => dispatch(showUpgradeModal('full-mixpedia'))}>
                                        <svg className="lock-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                                        + Unlock full support network
                                    </button>
                                )}
                                </>
                            ) : (
                                <div className="djpal-empty-hint">No DJ support data available yet</div>
                            )}
                        </div>}
                    </div>
                    </>)}

                    {/* Artist Relationships */}
                    {(!v2 || v2Tab === 'overview' || v2Tab === 'network') && (
                    <div className="djpal-two-col">
                        <div className="djpal-card">
                            {v2 ? (
                                <SectionHeader title="Artists they support" count={v2Tab === 'network' && artistsSupporting.length > 0 ? fmtNum(artistsSupportingTotal) : undefined} link={v2Tab === 'overview' && artistsSupporting.length > 0 && canAccess ? `See all ${fmtNum(artistsSupportingTotal)}` : null} onLink={() => setV2Tab('network')}>
                                    {v2Tab === 'network' && artistsSupporting.length > 0 && <CardSearch value={artistsTheySupportQuery} onChange={setArtistsTheySupportQuery} placeholder="Filter artists…" ariaLabel="Filter artists they support" />}
                                    {v2Tab === 'network' && artistsSupporting.length > 1 && <SortChips value={networkSort} onChange={setNetworkSort} modes={NETWORK_SORTS} labels={NETWORK_SORT_LABELS} />}
                                </SectionHeader>
                            ) : (
                            <div className="djpal-card-header">
                                <h3>Artists they support</h3>
                                {artistsSupporting.length > 0 && (
                                    <div className="djpal-card-header-actions">
                                        <CardSearch
                                            value={artistsTheySupportQuery}
                                            onChange={setArtistsTheySupportQuery}
                                            placeholder="Filter artists…"
                                            ariaLabel="Filter artists they support"
                                        />
                                        {canAccess && (
                                            <>
                                                <button className="djpal-card-expand-btn" title="List view — details & sorting" onClick={() => setArtistsModalKind('artistsSupporting')}>
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                                                </button>
                                                <button className="djpal-card-expand-btn" title="Browse & follow all" onClick={() => dispatch(openMixpediaArtistsGrid({ title: 'Artists they support', subtitle: artistName, artists: toGridArtists(artistsSupporting), from: 'artistPage' }))}>
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                            )}
                            <DJPalScrollableList maxHeight="250px">
                                {(v2Network && spBy ? (!spBy.loading && networkSupportRows.length === 0) : displayedArtistsSupporting.length === 0) && artistsTheySupportQuery.trim() && (
                                    <div className="djpal-empty-hint">No matches found.</div>
                                )}
                                {v2Network && spBy?.loading && !spBy.rows && netSkeleton}
                                {(v2 && v2Tab === 'network' ? networkSupportRows : v2Slice(displayedArtistsSupporting)).map((a, i) => (
                                    <React.Fragment key={entityId(a) || `support-${i}`}>
                                    <div className={`djpal-artist-bubble clickable${v2Network && networkOpenSupport === (entityId(a) || a.name) ? ' is-open' : ''}`} onClick={() => {
                                        if (!v2Network) { handleArtistClick(a.name, entityId(a)); return; }
                                        const k = entityId(a) || a.name;
                                        setNetworkOpenSupport((prev) => { if (prev !== k) trackEvent('network_artist_expanded', { list: 'support' }); return prev === k ? null : k; });
                                    }}>
                                        {v2 && <span className="v2-row-rank">{i + 1}</span>}
                                        {safeArtwork(a.artworkUrl) ? (
                                            <CoverArt className="artist-avatar" src={a.artworkUrl} covers={a.covers} size={24} alt={a.name} fallback={<div className="artist-avatar">{(a.name || '?').charAt(0).toUpperCase()}</div>} />
                                        ) : (
                                            <div className="artist-avatar">
                                                {(a.name || '?').charAt(0).toUpperCase()}
                                            </div>
                                        )}
                                        <span>{v2Network ? <span className="v2-row-name-link" onClick={(e) => { e.stopPropagation(); handleArtistClick(a.name, entityId(a)); }}>{hl(a.name)}</span> : hl(a.name)}</span>
                                        {v2 && rowCount(a, 'tracks') && <span className="v2-row-meta">{rowCount(a, 'tracks')}</span>}
                                        {v2Network && <span className="v2-row-chevron" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg></span>}
                                    </div>
                                    {v2Network && networkOpenSupport === (entityId(a) || a.name) && (
                                        <CoPlayedTracks artist={a} fetchTracksFor={(x) => fetchCoPlayedTracksFor(x, false)} onTrackClick={(t) => handleSearchTrack({ stopPropagation: () => {} }, t)} onMixClick={(mx) => handleMixClick({ id: entityId(mx), title: mx.eventName || mx.artist || '', link: mx.link })} />
                                    )}
                                    </React.Fragment>
                                ))}
                            </DJPalScrollableList>
                            {v2 && v2Tab === 'network' && canAccess && (
                                <AutoLoad onMore={() => (spBy ? spMore('by') : setNetworkSupportLimit(n => n + NETWORK_PAGE_SIZE))} hasMore={spBy ? !!spBy.hasMore : displayedArtistsSupporting.length > networkSupportLimit} />
                            )}
                            {!canAccess && !artistsTheySupportQuery.trim() && artistsSupportingTotal > FREE_RESULTS_LIMIT && (
                                <button className="djpal-tier-lock-msg" onClick={() => dispatch(showUpgradeModal({ feature: 'full-mixpedia', context: { total: artistsSupportingTotal } }))}>See all {artistsSupportingTotal} results with <GoatBadge /></button>
                            )}
                        </div>
                        <div className="djpal-card" ref={supportingCardRef}>
                            {v2 ? (
                                <SectionHeader title="Artists supporting them" count={v2Tab === 'network' && supportingArtists.length > 0 ? fmtNum(supportingArtistsTotal) : undefined} link={v2Tab === 'overview' && supportingArtists.length > 0 && canAccess ? `See all ${fmtNum(supportingArtistsTotal)}` : null} onLink={() => setV2Tab('network')}>
                                    {v2Tab === 'network' && supportingArtists.length > 0 && <CardSearch value={artistsSupportingThemQuery} onChange={setArtistsSupportingThemQuery} placeholder="Filter artists…" ariaLabel="Filter artists supporting them" />}
                                    {v2Tab === 'network' && supportingArtists.length > 1 && <SortChips value={networkSort} onChange={setNetworkSort} modes={NETWORK_SORTS} labels={NETWORK_SORT_LABELS} />}
                                </SectionHeader>
                            ) : (
                            <div className="djpal-card-header">
                                <h3>Artists supporting them</h3>
                                {supportingArtists.length > 0 && (
                                    <div className="djpal-card-header-actions">
                                        <CardSearch
                                            value={artistsSupportingThemQuery}
                                            onChange={setArtistsSupportingThemQuery}
                                            placeholder="Filter artists…"
                                            ariaLabel="Filter artists supporting them"
                                        />
                                        {canAccess && (
                                            <>
                                                <button className="djpal-card-expand-btn" title="List view — details & sorting" onClick={() => setArtistsModalKind('supportingThem')}>
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                                                </button>
                                                <button className="djpal-card-expand-btn" title="Browse & follow all" onClick={() => dispatch(openMixpediaArtistsGrid({ title: 'Artists supporting them', subtitle: artistName, artists: toGridArtists(supportingArtists), from: 'artistPage' }))}>
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                            )}
                            <DJPalScrollableList maxHeight="250px">
                                {(v2Network && spOf ? (!spOf.loading && networkSupportersRows.length === 0) : displayedSupportingArtists.length === 0) && artistsSupportingThemQuery.trim() && (
                                    <div className="djpal-empty-hint">No matches found.</div>
                                )}
                                {v2Network && spOf?.loading && !spOf.rows && netSkeleton}
                                {(v2 && v2Tab === 'network' ? networkSupportersRows : v2Slice(displayedSupportingArtists)).map((a, i) => (
                                    <React.Fragment key={entityId(a) || `supporting-${i}`}>
                                    <div className={`djpal-artist-bubble clickable${v2Network && networkOpenSupporting === (entityId(a) || a.name) ? ' is-open' : ''}`} onClick={() => {
                                        if (!v2Network) { handleArtistClick(a.name, entityId(a)); return; }
                                        const k = entityId(a) || a.name;
                                        setNetworkOpenSupporting((prev) => { if (prev !== k) trackEvent('network_artist_expanded', { list: 'supporting' }); return prev === k ? null : k; });
                                    }}>
                                        {v2 && <span className="v2-row-rank">{i + 1}</span>}
                                        {safeArtwork(a.artworkUrl) ? (
                                            <CoverArt className="artist-avatar" src={a.artworkUrl} covers={a.covers} size={24} alt={a.name} fallback={<div className="artist-avatar">{(a.name || '?').charAt(0).toUpperCase()}</div>} />
                                        ) : (
                                            <div className="artist-avatar">
                                                {(a.name || '?').charAt(0).toUpperCase()}
                                            </div>
                                        )}
                                        <span>{v2Network ? <span className="v2-row-name-link" onClick={(e) => { e.stopPropagation(); handleArtistClick(a.name, entityId(a)); }}>{hl(a.name)}</span> : hl(a.name)}</span>
                                        {v2 && rowCount(a, 'tracks') && <span className="v2-row-meta">{rowCount(a, 'tracks')}</span>}
                                        {v2Network && <span className="v2-row-chevron" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg></span>}
                                    </div>
                                    {v2Network && networkOpenSupporting === (entityId(a) || a.name) && (
                                        <CoPlayedTracks artist={a} fetchTracksFor={(x) => fetchCoPlayedTracksFor(x, true)} onTrackClick={(t) => handleSearchTrack({ stopPropagation: () => {} }, t)} onMixClick={(mx) => handleMixClick({ id: entityId(mx), title: mx.eventName || mx.artist || '', link: mx.link })} />
                                    )}
                                    </React.Fragment>
                                ))}
                            </DJPalScrollableList>
                            {v2 && v2Tab === 'network' && canAccess && (
                                <AutoLoad onMore={() => (spOf ? spMore('of') : setNetworkSupportersLimit(n => n + NETWORK_PAGE_SIZE))} hasMore={spOf ? !!spOf.hasMore : displayedSupportingArtists.length > networkSupportersLimit} />
                            )}
                            {!canAccess && !artistsSupportingThemQuery.trim() && supportingArtistsTotal > FREE_RESULTS_LIMIT && (
                                <button className="djpal-tier-lock-msg" onClick={() => dispatch(showUpgradeModal({ feature: 'full-mixpedia', context: { total: supportingArtistsTotal } }))}>See all {supportingArtistsTotal} results with <GoatBadge /></button>
                            )}
                        </div>
                    </div>
                    )}

                    {/* Mixes */}
                    {(!v2 || v2Tab === 'mixes') && (<>
                    {v2MixesTab && (
                        <ListToolbar
                            segments={[
                                { key: 'by', label: 'By them', count: paginationByArtist?.totalCount || mixesByArtist.length },
                                { key: 'featuring', label: 'Featuring them', count: paginationIncluding?.totalCount || mixesIncludingArtist.length },
                            ]}
                            value={v2MixesSeg}
                            onChange={setV2MixesSeg}
                        >
                            {(v2MixesSeg === 'by' ? mixesByView : mixesInclView).length > 1 && <SortChips value={v2MixesSort} onChange={setV2MixesSort} modes={MIX_SORTS} labels={MIX_SORT_LABELS} />}
                            {v2MixesSeg === 'by' && mixesByArtist.length > 0 && <CardSearch value={mixesByQuery} onChange={setMixesByQuery} placeholder="Filter mixes…" ariaLabel="Filter mixes by them" />}
                            {v2MixesSeg === 'featuring' && mixesIncludingArtist.length > 0 && <CardSearch value={mixesInclQuery} onChange={setMixesInclQuery} placeholder="Filter mixes…" ariaLabel="Filter mixes featuring them" />}
                        </ListToolbar>
                    )}
                    <div className={`djpal-two-col${v2MixesTab ? ' v2-single' : ''}`}>
                        {v2ShowMixesBy && <div className="djpal-card" ref={mixesByCardRef}>
                            {v2 ? (!v2MixesTab &&
                                <SectionHeader title="Mixes by them" link={mixesByArtist.length > 0 ? `See all${paginationByArtist?.totalCount ? ` ${fmtNum(paginationByArtist.totalCount)}` : ''}` : null} onLink={() => v2OpenMixes('by')} />
                            ) : (
                            <div className="djpal-card-header">
                                <h3>Mixes by them</h3>
                                {mixesByArtist.length > 0 && (
                                    <div className="djpal-card-header-actions">
                                        <CardSearch
                                            value={mixesByQuery}
                                            onChange={setMixesByQuery}
                                            placeholder="Filter mixes…"
                                            ariaLabel="Filter mixes by them"
                                        />
                                        <button className="djpal-card-expand-btn" title="Open list view" onClick={handleOpenMixesByModal}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                                        </button>
                                        <button className="djpal-card-expand-btn" title="Show all" onClick={handleShowAllMixesBy}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                            )}
                            {mixesByArtist.length > 0 ? (
                                <>
                                <DJPalScrollableList
                                    maxHeight="300px"
                                    onScrollBottom={canAccess && !v2 ? (isMixesByFiltered ? handleLoadMoreByFiltered : handleLoadMoreByArtist) : undefined}
                                >
                                    {isMixesByFiltered && mixesByFiltered.loading && renderMixesByList.length === 0 && (
                                        <div className="djpal-load-more-spinner"><span className="mix-play-spinner" /></div>
                                    )}
                                    {isMixesByFiltered && !mixesByFiltered.loading && renderMixesByList.length === 0 && (
                                        <div className="djpal-empty-hint">No matches for "{mixesByQuery.trim()}"</div>
                                    )}
                                    {mixesByView.map((mix, i) => {
                                        const mixArt = resolveMixCover(mix);
                                        const mixLabel = (mix.djName || mix.artist) ? `${mix.djName || mix.artist} - ${mix.title}` : mix.title;
                                        const isLoading = loadingMixId === mix.id;
                                        const isPlaying = playingMixId === mix.id;
                                        const canPlay = canPlayMix(mix);
                                        return (
                                            <div
                                                className="djpal-track-row clickable"
                                                key={`mix-by-${i}`}
                                                onContextMenu={openRowCtx(`mix-by-${i}`)}
                                                {...delayedClick(() => handleMixClick(mix))}
                                            >
                                                {v2 && <span className="v2-row-rank">{i + 1}</span>}
                                                <div
                                                    className={`track-artwork${canPlay ? ' clickable' : ''}`}
                                                    onClick={canPlay ? (e) => { e.stopPropagation(); handleMixPlay(mix, { items: mixesByView, index: i }); } : undefined}
                                                >
                                                    {canPlay && (
                                                        <div className={`mix-play-overlay${isLoading ? ' loading' : ''}${isPlaying ? ' playing' : ''}`}>
                                                            {isLoading ? <span className="mix-play-spinner" /> : (
                                                                <span className={`play-icon${isPlaying ? ' paused' : ''}`} aria-hidden="true">
                                                                    {isPlaying ? (
                                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                                            <rect x="6" y="4" width="4" height="16" rx="1" />
                                                                            <rect x="14" y="4" width="4" height="16" rx="1" />
                                                                        </svg>
                                                                    ) : (
                                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                                            <polygon points="6 3 20 12 6 21 6 3" />
                                                                        </svg>
                                                                    )}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                    {mixArt ? <CoverArt src={mixArt} covers={mix.covers} size={48} fallback={<div className="artwork-placeholder">&#9835;</div>} /> : <div className="artwork-placeholder">&#9835;</div>}
                                                </div>
                                                <div className="track-info">
                                                    <span className="track-label" title={mixLabel}>{hl(mix.title || mixLabel)}</span>
                                                    {((mix.artists?.length > 0 || mix.djName || mix.artist) || mix.date) && (
                                                        <span className="track-sublabel track-artist-line">
                                                            <MultiArtistText
                                                                artists={mix.artists}
                                                                fallbackText={mix.djName || mix.artist}
                                                                segmentClassName="clickable-artist"
                                                                onSelect={(a) => handleArtistClick(a.name, entityId(a), a.artworkUrl)}
                                                                renderName={hl}
                                                            />
                                                            {(mix.artists?.length > 0 || mix.djName || mix.artist) && mix.date ? ' · ' : ''}
                                                            {mix.date}
                                                        </span>
                                                    )}
                                                </div>
                                                {v2MixesTab && mix.declaredTrackCount > 0 && <span className="v2-row-meta">{fmtNum(mix.declaredTrackCount)} tracks</span>}
                                                <MixLikeButton mix={mix} />
                                                <MixContextMenu mix={mix} openAtPoint={rowCtxFor(`mix-by-${i}`)} />
                                            </div>
                                        );
                                    })}
                                    {(isMixesByFiltered ? loadingMoreByFiltered : loadingMoreBy) && !(v2 && canAccess) && (
                                        <div className="djpal-load-more-spinner"><span className="mix-play-spinner" /></div>
                                    )}
                                </DJPalScrollableList>
                                {v2 && canAccess && (
                                    <AutoLoad onMore={isMixesByFiltered ? handleLoadMoreByFiltered : handleLoadMoreByArtist} loading={isMixesByFiltered ? loadingMoreByFiltered : loadingMoreBy} hasMore={!!paginationByArtist?.hasNext} />
                                )}
                                {!canAccess && !isMixesByFiltered && mixesByArtist.length > FREE_RESULTS_LIMIT && (
                                    <button className="djpal-tier-lock-msg" onClick={() => dispatch(showUpgradeModal({ feature: 'full-mixpedia', context: { total: mixesByArtist.length } }))}>See all {mixesByArtist.length} results with <GoatBadge /></button>
                                )}
                                {!canAccess && isMixesByFiltered && mixesByFiltered.mixes.length > FREE_RESULTS_LIMIT && (
                                    <button className="djpal-tier-lock-msg" onClick={() => dispatch(showUpgradeModal('full-mixpedia'))}>See all matches with <GoatBadge /></button>
                                )}
                                </>
                            ) : (
                                <div className="djpal-empty-hint">No DJ mixes found</div>
                            )}
                        </div>}
                        {v2ShowMixesFeat && <div className="djpal-card" ref={featuringCardRef}>
                            {v2 ? (!v2MixesTab &&
                                <SectionHeader title="Mixes featuring them" link={mixesIncludingArtist.length > 0 ? `See all${paginationIncluding?.totalCount ? ` ${fmtNum(paginationIncluding.totalCount)}` : ''}` : null} onLink={() => v2OpenMixes('featuring')} />
                            ) : (
                            <div className="djpal-card-header">
                                <h3>Mixes featuring them</h3>
                                {mixesIncludingArtist.length > 0 && (
                                    <div className="djpal-card-header-actions">
                                        <CardSearch
                                            value={mixesInclQuery}
                                            onChange={setMixesInclQuery}
                                            placeholder="Filter mixes…"
                                            ariaLabel="Filter mixes featuring them"
                                        />
                                        <button className="djpal-card-expand-btn" title="Open list view" onClick={handleOpenMixesFeaturingModal}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                                        </button>
                                        <button className="djpal-card-expand-btn" title="Show all" onClick={handleShowAllMixesFeaturing}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                            )}
                            {mixesIncludingArtist.length > 0 ? (
                                <>
                                <DJPalScrollableList
                                    maxHeight="300px"
                                    onScrollBottom={canAccess && !v2 ? (isMixesInclFiltered ? handleLoadMoreInclFiltered : handleLoadMoreIncluding) : undefined}
                                >
                                    {isMixesInclFiltered && mixesInclFiltered.loading && renderMixesInclList.length === 0 && (
                                        <div className="djpal-load-more-spinner"><span className="mix-play-spinner" /></div>
                                    )}
                                    {isMixesInclFiltered && !mixesInclFiltered.loading && renderMixesInclList.length === 0 && (
                                        <div className="djpal-empty-hint">No matches for "{mixesInclQuery.trim()}"</div>
                                    )}
                                    {mixesInclView.map((mix, i) => {
                                        const mixArt = resolveMixCover(mix);
                                        const mixLabel = (mix.djName || mix.artist) ? `${mix.djName || mix.artist} - ${mix.title}` : mix.title;
                                        const isLoading = loadingMixId === mix.id;
                                        const isPlaying = playingMixId === mix.id;
                                        const canPlay = canPlayMix(mix);
                                        return (
                                            <div
                                                className="djpal-track-row clickable"
                                                key={`mix-feat-${i}`}
                                                onContextMenu={openRowCtx(`mix-feat-${i}`)}
                                                {...delayedClick(() => handleMixClick(mix))}
                                            >
                                                {v2 && <span className="v2-row-rank">{i + 1}</span>}
                                                <div
                                                    className={`track-artwork${canPlay ? ' clickable' : ''}`}
                                                    onClick={canPlay ? (e) => { e.stopPropagation(); handleMixPlay(mix, { items: mixesInclView, index: i }); } : undefined}
                                                >
                                                    {canPlay && (
                                                        <div className={`mix-play-overlay${isLoading ? ' loading' : ''}${isPlaying ? ' playing' : ''}`}>
                                                            {isLoading ? <span className="mix-play-spinner" /> : <span className={`play-icon${isPlaying ? ' paused' : ''}`}>{isPlaying ? <PauseIcon /> : <PlayIcon />}</span>}
                                                        </div>
                                                    )}
                                                    {mixArt ? <CoverArt src={mixArt} covers={mix.covers} size={48} fallback={<div className="artwork-placeholder">&#9835;</div>} /> : <div className="artwork-placeholder">&#9835;</div>}
                                                </div>
                                                <div className="track-info">
                                                    <span className="track-label" title={mixLabel}>{hl(mix.title || mixLabel)}</span>
                                                    {((mix.artists?.length > 0 || mix.djName || mix.artist) || mix.date) && (
                                                        <span className="track-sublabel track-artist-line">
                                                            <MultiArtistText
                                                                artists={mix.artists}
                                                                fallbackText={mix.djName || mix.artist}
                                                                segmentClassName="clickable-artist"
                                                                onSelect={(a) => handleArtistClick(a.name, entityId(a), a.artworkUrl)}
                                                                renderName={hl}
                                                            />
                                                            {(mix.artists?.length > 0 || mix.djName || mix.artist) && mix.date ? ' · ' : ''}
                                                            {mix.date}
                                                        </span>
                                                    )}
                                                </div>
                                                {v2MixesTab && mix.declaredTrackCount > 0 && <span className="v2-row-meta">{fmtNum(mix.declaredTrackCount)} tracks</span>}
                                                <MixLikeButton mix={mix} />
                                                <MixContextMenu mix={mix} openAtPoint={rowCtxFor(`mix-feat-${i}`)} />
                                            </div>
                                        );
                                    })}
                                    {(isMixesInclFiltered ? loadingMoreInclFiltered : loadingMoreIncl) && !(v2 && canAccess) && (
                                        <div className="djpal-load-more-spinner"><span className="mix-play-spinner" /></div>
                                    )}
                                </DJPalScrollableList>
                                {v2 && canAccess && (
                                    <AutoLoad onMore={isMixesInclFiltered ? handleLoadMoreInclFiltered : handleLoadMoreIncluding} loading={isMixesInclFiltered ? loadingMoreInclFiltered : loadingMoreIncl} hasMore={!!paginationIncluding?.hasNext} />
                                )}
                                {!canAccess && !isMixesInclFiltered && mixesIncludingArtist.length > FREE_RESULTS_LIMIT && (
                                    <button className="djpal-tier-lock-msg" onClick={() => dispatch(showUpgradeModal({ feature: 'full-mixpedia', context: { total: mixesIncludingArtist.length } }))}>See all {mixesIncludingArtist.length} results with <GoatBadge /></button>
                                )}
                                {!canAccess && isMixesInclFiltered && mixesInclFiltered.mixes.length > FREE_RESULTS_LIMIT && (
                                    <button className="djpal-tier-lock-msg" onClick={() => dispatch(showUpgradeModal('full-mixpedia'))}>See all matches with <GoatBadge /></button>
                                )}
                                </>
                            ) : (
                                <div className="djpal-empty-hint">No mixes featuring them found</div>
                            )}
                        </div>}
                    </div>
                    </>)}
                    </>)}
                </div>
            )}

            {artistsModalKind && (() => {
                const isSupporting = artistsModalKind === 'supportingThem';
                const list = isSupporting ? supportingArtists : artistsSupporting;
                const subjectName = artistName || '';

                // Public-API sanitizer renames `publicId` → `id` on every
                // entity, so the client-side artist objects use `a.id` to
                // hold the publicId. Same for the subject artist on this
                // page — `artist?.id` is its publicId.
                const subjectPublicId = entityId(artist) || artistPublicId || null;

                const fetchTracksFor = (a) => fetchCoPlayedTracksFor(a, isSupporting);

                return (
                    <ArtistsListModal
                        title={isSupporting ? 'Artists supporting them' : 'Artists they support'}
                        subtitle={subjectName ? subjectName : null}
                        artists={list}
                        getSubtitle={(a) => {
                            const n = a.tracksCount;
                            if (!n) return null;
                            return `played ${n} ${n === 1 ? 'track' : 'tracks'}`;
                        }}
                        fetchTracksFor={fetchTracksFor}
                        onTrackClick={(t) => handleSearchTrack({ stopPropagation: () => {} }, t)}
                        onMixClick={(mx) => handleMixClick({
                            id: entityId(mx),
                            title: mx.eventName || mx.artist || '',
                            link: mx.link,
                        })}
                        onArtistClick={(a) => handleArtistClick(a.name, entityId(a))}
                        onClose={() => setArtistsModalKind(null)}
                    />
                );
            })()}

            {tracksModalKind && (() => {
                const isPlayed = tracksModalKind === 'played';
                const subjectPublicId = entityId(artist) || artistPublicId || null;
                return (
                    <TracksListModal
                        title={isPlayed ? 'Tracks they played' : 'Their tracks played by others'}
                        subtitle={artistName || null}
                        artistName={artistName}
                        artistPublicId={subjectPublicId}
                        relation={isPlayed ? 'played' : 'by'}
                        dateRange={dateRange}
                        initialTracks={isPlayed ? tracksPlayedAll : tracksPlayedByOthersAll}
                        initialPagination={isPlayed ? paginationByArtist : paginationIncluding}
                        totalCount={isPlayed
                            ? (statsByArtist?.totalTracksPlayed ?? null)
                            : (statsIncluded?.totalTracksSupported ?? null)}
                        onTrackClick={(t) => handleSearchTrack({ stopPropagation: () => {} }, t)}
                        onMixClick={(mx) => handleMixClick({
                            id: entityId(mx),
                            title: mx.title || mx.eventName || mx.artist || '',
                            link: mx.link,
                            djName: mx.djName,
                            artist: mx.artist,
                        })}
                        onArtistClick={(a) => handleArtistClick(a.name, entityId(a))}
                        onClose={() => setTracksModalKind(null)}
                    />
                );
            })()}

            {mixesModalKind && (() => {
                const isBy = mixesModalKind === 'byArtist';
                const subjectPublicId = entityId(artist) || artistPublicId || null;
                return (
                    <MixesListModal
                        title={isBy ? 'Mixes by them' : 'Mixes featuring them'}
                        subtitle={artistName || null}
                        artistName={artistName}
                        artistPublicId={subjectPublicId}
                        section={isBy ? 'byArtist' : 'including'}
                        dateRange={dateRange}
                        initialMixes={isBy ? mixesByArtist : mixesIncludingArtist}
                        initialPagination={isBy ? paginationByArtist : paginationIncluding}
                        totalCount={isBy
                            ? (paginationByArtist?.totalCount ?? mixesByArtist.length)
                            : (paginationIncluding?.totalCount ?? mixesIncludingArtist.length)}
                        onMixClick={(mx) => handleMixClick(mx)}
                        onTrackClick={(t) => handleSearchTrack({ stopPropagation: () => {} }, t)}
                        onArtistClick={(a) => handleArtistClick(a.name, entityId(a), a.artworkUrl)}
                        onClose={() => setMixesModalKind(null)}
                    />
                );
            })()}
        </div>
    );
};

export default ArtistSearch;
